/**
 * UnifiedAppointmentModal.tsx
 *
 * Single-shared appointment modal used for both create and view/edit flows.
 * Mirrors the legacy `services/scheduler-appt-details.php` feature surface:
 *   - Header with patient/plan context, computed status, badges, patient actions
 *   - Toolbar: Save / Copy / Delete
 *   - Validation strip: Errors / Warnings / Conflicts
 *   - Details: date, duration, start, end, facility, group, type, title, first-appt, comp
 *   - Status: 8 timestamp rows (Contact Attempted, Confirmed, Check In, Check Back,
 *     Check Out, Cancelled, No Show, Waiting List) with per-row Now/Clear and
 *     Send Confirmation handoff
 *   - Notes: Appointment Type Note (read), Appointment Notes (edit), Patient Notes (read)
 *   - People / Resources inline add / remove
 *   - Required Products / Pricing / Purchase (opens ChargePaymentModal)
 *   - Shortcodes
 *   - Appointment History
 *
 * Kept deliberately large so the parity surface stays discoverable in one file.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@/lib/zodResolver';
import { optionalLegacyForeignKey } from '@/lib/legacyZod';
import {
  getTimezoneAbbreviation,
  pacificToWallClock,
  utcToWallClock,
  reprojectDateTime,
  wallClockPartsInTz,
} from '@/lib/facilityTimezone';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, CalendarSearch, CheckCircle2, ChevronDown, ChevronUp, Clock, Copy as CopyIcon, ExternalLink, FileText, Loader2, LogIn, LogOut, MapPin, MessageSquare, Plus, ShoppingCart, Tag, Trash2, User, UserPlus, Users, X } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { TimePicker } from '@/components/ui/TimePicker';
import { Select } from '@/components/ui/Select';
import { DatePicker } from '@/components/ui/DatePicker';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/v5/components/ui/switch';
import { useNoteForm } from '@/v5/contexts/NoteFormContext';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { PatientMemberBadge } from '@/components/patients/MemberBadge';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { ChargePaymentModal, type ChargePaymentPrefillItem } from '@/pages/payments/ChargePaymentModal';
import { AddProductsModal } from '@/pages/appointments/AddProductsModal';
import AppointmentProductsPanel from '@/pages/appointments/AppointmentProductsPanel';
import PatientInventoryPanel from '@/pages/appointments/PatientInventoryPanel';
import { AppointmentLinkedNotes } from '@/pages/appointments/AppointmentLinkedNotes';
import { ChangePatientButton } from '@/pages/appointments/ChangePatientButton';
import {
  getAppointmentProducts as fetchNewAppointmentProducts,
  getPatientProductTypes,
  compAppointmentProduct as compAllProductsApi,
  type AppointmentProductsResponse as NewApptProductsResponse,
  type ProductMode,
} from '@/services/patientProductTypes.service';
import { parseCurrency, formatCurrency, getAge } from '@/lib/utils';
import {
  RESOURCE_CONFLICT_TYPES,
  buildConflictHeading,
  categorizeConflicts,
  formatResourceConflictLine,
} from '@/lib/appointmentConflicts';
import { appointmentsService, getCalendarSettings, type Appointment, type AppointmentHistoryEntry, type AppointmentShortcode, type AppointmentStatus, type AppointmentTypePatientDefaults, type ConflictInfo, type CreateAppointmentPayload, type Resource } from '@/services/appointments.service';
import { promptAndSendBookedSmsUpdate } from './promptBookedSmsUpdate';
import {
  autoPickedPlan,
  planAfterPatientSelect,
  planAfterPick,
  planAfterPlansRefresh,
  planAfterTypeChange,
  planFieldView,
  planOptions as buildPlanOptions,
  savedRowAlreadyMissesRequirement,
  withPlanRequirement,
  type PlanFieldInput,
  type PlanSelection,
} from './appointmentPlanRequirement';
import { getPlansByPatient } from '@/services/plans.service';
import { HistoryActorText } from './appointmentHistoryActor';
import {
  buildDiffRows,
  buildSnapshotRows,
  historyDate,
  type HistoryDiffRow,
  type HistorySnapshotRow,
} from './appointmentHistoryRows';
import { listFacilities } from '@/services/admin.service';
import { listTasks, type Task as TaskRow } from '@/services/tasks.service';
import { listPatientForms } from '@/services/patientForms.service';
import { getIntakePatient, type IntakePatientPlan } from '@/services/leadIntake.service';
import { PlanCreateModal } from '@/pages/plans/PlanCreateModal';
import { PatientCreateModal } from '@/pages/leads/PatientCreateModal';
import type { CreatedIntakePatient } from '@/pages/leads/NewLeadPage';
import { useAuthStore } from '@/store/authStore';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';
import { extractFieldErrors, getErrorCode, getErrorMessage } from '@/services/apiClient';
import type { PatientSummary } from '@/types/patient.types';
import type { SelectOption, BadgeVariant } from '@/types/common.types';
import {
  ResourceRowList,
  RESOURCE_TYPE_LABELS,
  RESOURCE_TYPE_TO_ROLE_KEY,
  type ResourceRow,
} from '@/components/scheduling/ResourceRowList';

// ─── Constants / helpers ──────────────────────────────────────────────────────

const STATUS_LABEL: Record<AppointmentStatus, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  completed: 'Completed',
  no_show: 'No Show',
  cancelled: 'Cancelled',
  contact_attempted: 'Contact Attempted',
  waiting_list: 'Waiting List',
  reschedule_pending: 'Reschedule Pending',
  checked_in: 'Checked In',
  checked_back: 'Checked Back',
  action_required: 'Action Required',
};

const STATUS_BADGE: Record<AppointmentStatus, BadgeVariant> = {
  scheduled: 'info',
  confirmed: 'success',
  completed: 'neutral',
  no_show: 'warning',
  cancelled: 'error',
  contact_attempted: 'warning',
  waiting_list: 'info',
  reschedule_pending: 'warning',
  checked_in: 'success',
  checked_back: 'info',
  action_required: 'warning',
};

const STATUS_TEXT_COLOR: Record<AppointmentStatus, string> = {
  scheduled: 'text-blue-600',
  confirmed: 'text-green-600',
  completed: 'text-slate-500',
  no_show: 'text-amber-600',
  cancelled: 'text-red-600',
  contact_attempted: 'text-amber-600',
  waiting_list: 'text-blue-600',
  reschedule_pending: 'text-amber-600',
  checked_in: 'text-green-600',
  checked_back: 'text-blue-600',  action_required: 'text-amber-600',
};

const PACIFIC_TZ = 'America/Los_Angeles';

function getNowPacific(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}:${g('second')}`;
}

/** Extract "YYYY-MM-DD" from a datetime string (ISO-like or space-separated). */
function toDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

/** Extract "HH:MM" from a datetime string. Handles both `T` and space separators. */
function toTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const tIdx = iso.indexOf('T');
  if (tIdx >= 0) return iso.slice(tIdx + 1, tIdx + 6);
  const spaceIdx = iso.indexOf(' ');
  if (spaceIdx >= 0) return iso.slice(spaceIdx + 1, spaceIdx + 6);
  return iso.slice(11, 16);
}

/** Convert a UTC ISO string from the API to a Pacific ISO string for local state storage. */
function utcIsoToPacificIso(utcIso: string | null | undefined): string | null {
  if (!utcIso) return null;
  const wall = utcToWallClock(utcIso, PACIFIC_TZ);
  if (!wall) return utcIso.slice(0, 16) + ':00';
  return `${wall.date}T${wall.time}:00`;
}

/** Compute end time from start time + duration using simple clock arithmetic. */
function computeEndTime(startTime: string, durationMinutes: number): string {
  const [h, m] = startTime.split(':').map(Number);
  const total = h * 60 + m + durationMinutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Duration (min) from same-day start/end "HH:MM"; null if end is not after start. */
function computeDurationFromTimes(startTime: string, endTime: string): number | null {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : null;
}

function formatTimeDisplay(iso: string | null | undefined): string {
  if (!iso) return '';
  const clean = iso.replace(' ', 'T');
  const d = new Date(clean.endsWith('Z') ? clean : clean + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * The API writes the literal `'Not Set'` placeholder into
 * `scheduledAppointmentCancelledReason` when no reason has been recorded
 * (SchedulerWorkflowService). Treat it as blank so the details box starts empty
 * and the user is forced to type a real reason.
 */
function normalizeCancelReason(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  return text.toLowerCase() === 'not set' ? '' : text;
}

/** Build a patient summary for the search input chip. */
function buildSelectedPatient(input: {
  patientId?: number | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayId?: string | null;
  dateOfBirth?: string | null;
}): PatientSummary | null {
  const { patientId, displayName, firstName, lastName, displayId = null, dateOfBirth = null } = input;
  if (!patientId) return null;
  const resolved = (displayName ?? `${firstName ?? ''} ${lastName ?? ''}`).trim();
  if (!resolved) return null;
  return {
    patientId,
    accountId: 0,
    firstName: (firstName ?? resolved).trim(),
    lastName: (lastName ?? '').trim(),
    displayName: resolved,
    displayId,
    dateOfBirth,
    phone: null,
    homePhone: null,
    cellPhone: null,
    email: null,
    photoUrl: null,
    status: 'active',
    activePlanId: null,
    activePlanStage: null,
  };
}

// ─── ResourceRowList ──────────────────────────────────────────────────────────
// Shared component — see @/components/scheduling/ResourceRowList.tsx

// ─── Zod schema ───────────────────────────────────────────────────────────────

// `optionalLegacyForeignKey` accepts `0` as the legacy "no value" sentinel
// (see `@/lib/legacyZod`). Used for `patientId`, `patientPlanId`, and
// `appointmentTypeGroupId` per docs/audit/appt-details-parity.md §5.A.

const schema = z.object({
  name: z.string().trim().min(1, 'Title is required'),
  patientId: optionalLegacyForeignKey,
  patientPlanId: optionalLegacyForeignKey,
  appointmentTypeGroupId: optionalLegacyForeignKey,
  appointmentTypeId: z.number().int().positive().optional(),
  facilityId: z.number().int().positive().optional(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Select a date'),
  startTime: z.string().min(1, 'Select a start time'),
  endTime: z.string().optional(),
  duration: z.coerce.number().int().positive('Enter a duration').min(1, 'Enter a duration'),
  reason: z.string().max(4000).optional(),
  waitList: z.boolean().optional(),
  firstAppointment: z.boolean().optional(),
  compAppointment: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

// Maps server validation keys → form field names so Laravel `:attribute`
// errors can be shown inline on the matching control.
const SERVER_FIELD_TO_FORM: Record<string, keyof FormValues> = {
  name: 'name',
  scheduledAppointmentName: 'name',
  startTime: 'startTime',
  scheduledAppointmentStartTime: 'startTime',
  endTime: 'endTime',
  scheduledAppointmentEndTime: 'endTime',
  duration: 'duration',
  scheduledAppointmentDuration: 'duration',
  patientId: 'patientId',
  patientID: 'patientId',
  patientPlanId: 'patientPlanId',
  patientPlanID: 'patientPlanId',
  planId: 'patientPlanId',
  appointmentTypeId: 'appointmentTypeId',
  appointmentTypeID: 'appointmentTypeId',
  facilityId: 'facilityId',
  facilityID: 'facilityId',
  reason: 'reason',
  scheduledAppointmentReason: 'reason',
  waitList: 'waitList',
  scheduledAppointmentWaitList: 'waitList',
  firstAppointment: 'firstAppointment',
  compAppointment: 'compAppointment',
};

// User-facing labels for the validation banner. Falls back to the raw
// server key when an entry is missing (e.g. a brand-new field).
const FIELD_LABEL: Record<string, string> = {
  name: 'Title',
  startTime: 'Start time',
  endTime: 'End time',
  duration: 'Duration',
  date: 'Date',
  patientId: 'Patient',
  patientPlanId: 'Patient plan',
  appointmentTypeId: 'Type',
  appointmentTypeGroupId: 'Group',
  facilityId: 'Facility',
  reason: 'Reason',
  waitList: 'Wait list',
  firstAppointment: 'First appointment',
  compAppointment: 'Comp products',
  resourceIds: 'Resources',
  resourceIDs: 'Resources',
  shortcodes: 'Shortcodes',
};

// Rewrite Laravel's "The {field} field is required." style messages so the
// inline error reads naturally next to the control (e.g. "Title is required.").
function humanizeServerMessage(serverField: string, message: string): string {
  const label = FIELD_LABEL[SERVER_FIELD_TO_FORM[serverField] ?? serverField] ?? serverField;
  return message
    .replace(new RegExp(`^The ${serverField} field`, 'i'), `${label} field`)
    .replace(new RegExp(`^The ${serverField}\\b`, 'i'), label);
}

// ─── Section helpers ──────────────────────────────────────────────────────────

function SectionHeader({
  title,
  children,
  collapsed,
  onToggle,
  headerClassName,
}: {
  title: string;
  children?: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
  headerClassName?: string;
}) {
  return (
    <div
      className={headerClassName ?? "flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-t-md cursor-pointer select-none"}
      onClick={onToggle}
      role={onToggle ? 'button' : undefined}
    >
      {/*
        `min-w-0` on the title and `shrink-0` on the trailing group is what stops the
        count badge riding over the collapse chevron on a narrow header — the badge and
        the arrow were both being compressed by a title that would not yield
        (SCRUM-1408). Unprefixed because it changes nothing at a width where the header
        already fits, and the flex defaults it corrects are wrong at every width.
      */}
      <h3 className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-slate-700">{title}</h3>
      <div className="flex shrink-0 items-center gap-2">
        {children}
        {onToggle ? collapsed ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronUp className="h-4 w-4 text-slate-400" /> : null}
      </div>
    </div>
  );
}

function SectionShell({
  title,
  collapsed,
  onToggle,
  right,
  children,
  headerClassName,
}: {
  title: string;
  collapsed?: boolean;
  onToggle?: () => void;
  right?: React.ReactNode;
  children: React.ReactNode;
  headerClassName?: string;
}) {
  return (
    <section className="mb-4">
      <SectionHeader title={title} collapsed={collapsed} onToggle={onToggle} headerClassName={headerClassName}>
        {right}
      </SectionHeader>
      {!collapsed && (
        <div className="p-3 border border-t-0 border-slate-200 rounded-b-md bg-white">{children}</div>
      )}
    </section>
  );
}

// ─── StatusRow ────────────────────────────────────────────────────────────────

interface StatusRowProps {
  label: string;
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  trailing?: React.ReactNode;
  facilityTz?: string;
}

function pacificIsoToFacilityLocal(iso: string | null | undefined, facilityTz: string): string {
  if (!iso) return '';
  const dateStr = iso.slice(0, 10);
  const timeStr = iso.slice(11, 16);
  if (!dateStr || !timeStr) return iso.slice(0, 16);
  if (facilityTz === PACIFIC_TZ) return iso.slice(0, 16);
  const { date: wDate, time: wTime } = pacificToWallClock(dateStr, timeStr, facilityTz);
  return `${wDate}T${wTime}`;
}

/**
 * Pacific state value → the naive facility-local string the API expects on write.
 *
 * Status timestamps live in component state as Pacific wall-clock (that is what
 * `getNowPacific` stamps and what `utcIsoToPacificIso` decodes), but the API
 * parses every naive datetime it receives *in the facility's zone* before
 * converting back to Pacific for storage
 * (`SchedulerWorkflowService::normalizeDateTime`). Posting the raw Pacific
 * string therefore shifted each timestamp by the Pacific↔facility offset on
 * every save, which is why a check-in that read 3:00 PM came back 3 hours off
 * once the modal reloaded.
 */
function pacificIsoToFacilityIso(iso: string | null, facilityTz: string): string | null {
  if (!iso) return null;
  const local = pacificIsoToFacilityLocal(iso, facilityTz);
  return local ? `${local.slice(0, 16)}:00` : null;
}

function facilityLocalToPacificIso(localValue: string, facilityTz: string): string | null {
  if (!localValue) return null;
  if (facilityTz === PACIFIC_TZ) {
    return localValue.length === 16 ? `${localValue}:00` : localValue;
  }
  const dateStr = localValue.slice(0, 10);
  const timeStr = localValue.slice(11, 16);
  const { date: pDate, time: pTime } = reprojectDateTime(dateStr, timeStr, facilityTz, PACIFIC_TZ);
  return `${pDate}T${pTime}:00`;
}

function StatusRow({ label, value, onChange, disabled, trailing, facilityTz }: StatusRowProps) {
  const hasValue = !!value;
  const tz = facilityTz || PACIFIC_TZ;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      onChange(getNowPacific());
    } else {
      onChange(null);
    }
  };

  return (
    <div className="py-1">
      <div className="flex items-center gap-1.5">
        <Switch
          checked={hasValue}
          onCheckedChange={handleToggle}
          disabled={disabled}
        />
        <span className="text-xs font-medium text-slate-600 truncate">{label}</span>
        {trailing}
      </div>
      <div className="mt-0.5 pl-9 h-6">
        {hasValue && (
          <input
            type="datetime-local"
            value={pacificIsoToFacilityLocal(value, tz)}
            onChange={(event) => {
              const pacific = facilityLocalToPacificIso(event.target.value, tz);
              onChange(pacific);
            }}
            disabled={disabled}
            className="w-full h-6 text-[11px] border border-slate-200 rounded px-1.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50"
          />
        )}
      </div>
    </div>
  );
}

// ─── ShortcodesPanel ──────────────────────────────────────────────────────────

function ShortcodesPanel({
  shortcodes,
  values,
  onChange,
}: {
  shortcodes: AppointmentShortcode[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  if (shortcodes.length === 0) {
    return <p className="text-xs text-slate-400">No appointment shortcodes configured.</p>;
  }

  return (
    <div className="space-y-2">
      {shortcodes.map((sc) => {
        const key = String(sc.shortcodeId);
        return (
          <div key={sc.shortcodeId} className="flex items-center gap-2">
            <span className="inline-flex items-center rounded bg-slate-100 px-2 py-1 text-xs font-mono text-slate-700 whitespace-nowrap">
              {`\${${sc.shortcodeText}}`}
            </span>
            <Input
              value={values[key] ?? sc.contentText ?? ''}
              onChange={(event) => onChange({ ...values, [key]: event.target.value })}
              placeholder={sc.shortcodeTitle || sc.defaultContentText || ''}
              maxLength={255}
              className="flex-1"
            />
            {values[key]?.trim() ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => window.open(values[key])}
                title={sc.shortcodeTitle || 'Open link'}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ─── HistoryPanel ─────────────────────────────────────────────────────────────

function HistoryHeader({
  entry,
  prefix,
}: {
  entry: AppointmentHistoryEntry;
  prefix: 'Created' | 'Edited';
}) {
  return (
    <span>
      {prefix} {entry.auditStartDate ? historyDate(entry.auditStartDate, entry.facilityIanaTimezone) : '(unknown date)'}
      <HistoryActorText entry={entry} />
    </span>
  );
}

function HistoryPanel({
  history,
  loading,
}: {
  history: AppointmentHistoryEntry[];
  loading: boolean;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (loading) {
    return <p className="text-xs text-slate-400">Loading appointment history…</p>;
  }
  if (history.length === 0) {
    return <p className="text-xs text-slate-400">No audit history for this appointment.</p>;
  }

  // Build rendered rows in the same order legacy renders them: index 0
  // is "Created", every later index is "Edited" with its diff vs the
  // immediately prior revision. We skip an "Edited" row that has no
  // detectable change (matches legacy's `if ($rowContent != "")` guard).
  const rendered: Array<{
    key: string;
    prefix: 'Created' | 'Edited';
    entry: AppointmentHistoryEntry;
    snapshot?: HistorySnapshotRow[];
    diff?: HistoryDiffRow[];
  }> = [];
  history.forEach((entry, idx) => {
    const key = `${entry.auditRecordId}-${entry.appointmentId}-${entry.auditStartDate ?? idx}`;
    if (idx === 0) {
      rendered.push({ key, prefix: 'Created', entry, snapshot: buildSnapshotRows(entry) });
      return;
    }
    const diff = buildDiffRows(entry, history[idx - 1]);
    if (diff.length === 0) {
      return;
    }
    rendered.push({ key, prefix: 'Edited', entry, diff });
  });

  if (rendered.length === 0) {
    return <p className="text-xs text-slate-400">No detectable revisions for this appointment.</p>;
  }

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
      {rendered.map((row) => {
        const isCollapsed = expanded[row.key] !== true;
        return (
          <div key={row.key} className="rounded-md border border-slate-200">
            <button
              type="button"
              onClick={() => setExpanded((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
              className="w-full text-left px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-t-md flex items-center justify-between gap-3"
            >
              <span className="text-xs font-semibold uppercase text-slate-700">
                <HistoryHeader entry={row.entry} prefix={row.prefix} />
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
              />
            </button>
            {!isCollapsed ? (
              <div className="px-3 py-2 bg-white rounded-b-md">
                {row.prefix === 'Created' && row.snapshot ? (
                  <table className="w-full text-xs">
                    <tbody>
                      {row.snapshot.map((sr) => (
                        <tr key={sr.label} className="align-top">
                          <td className="text-right font-semibold text-blue-700 pr-3 py-0.5 whitespace-nowrap">
                            {sr.label}:
                          </td>
                          <td className="text-slate-700 py-0.5">{sr.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
                {row.prefix === 'Edited' && row.diff ? (
                  <table className="w-full text-xs">
                    <tbody>
                      {row.diff.map((dr) => (
                        <tr key={dr.label} className="align-top">
                          <td className="text-right font-semibold text-blue-700 pr-3 py-0.5 whitespace-nowrap">
                            {dr.label}:
                          </td>
                          <td className="text-slate-700 py-0.5 max-w-[180px] break-words">{dr.before}</td>
                          <td className="text-center text-slate-400 px-1">→</td>
                          <td className="text-slate-700 py-0.5 max-w-[180px] break-words">{dr.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Whether every product on the appointment is comped, or null when it has
 * none. Products owed from other appointments (`purchaseOthers`) don't count.
 */
function allProductsComped(data: NewApptProductsResponse): boolean | null {
  const slots = Object.values(data.products).flatMap((pt) => [
    ...(pt.pending ?? []),
    ...(pt.pendingAvailable ?? []),
    ...(pt.disbursed ?? []),
  ]);
  if (slots.length === 0) return null;
  return slots.every((slot) => slot.type === 'comp');
}

/**
 * Builds the seed for `ChargePaymentModal.prefillItems`. Mirrors legacy
 * `loadApptPaymentProducts(apptID)` from `payments-test.js`: pending
 * SAP rows are grouped by productTypeID with `purchaseQuantity` summed
 * across slots, and the patient's `purchaseOthers` rows from sibling
 * appointments are included so they get reconciled in the same charge.
 *
 * Only passes product IDs and quantities — the ChargePaymentModal looks
 * up catalog retail/member pricing and applies member discounts via
 * discountType=4 automatically.
 */
function buildPrefillItems(
  data: NewApptProductsResponse | null
): ChargePaymentPrefillItem[] {
  if (!data) {
    return [];
  }

  type Group = {
    quoteItemCostId: number;
    quantity: number;
    productTypeName: string;
    scheduledAppointmentProductsIds: number[];
  };

  const byType = new Map<number, Group>();

  for (const pt of Object.values(data.products)) {
    // A product type with no active cost item can't be priced or mapped to a
    // real product — never seed a charge line for it (a $0 line for a product
    // that does not exist). The user must switch the required set instead.
    if (pt.hasCostItem === false || !(Number(pt.productID) > 0)) {
      continue;
    }
    const ptId = Number(pt.productTypeID);
    for (const slot of pt.pending ?? []) {
      if (slot.type === 'comp') continue;
      const existing = byType.get(ptId);
      const sapId = slot.scheduledAppointmentProductsID ? Number(slot.scheduledAppointmentProductsID) : 0;
      if (existing) {
        existing.quantity += 1;
        if (sapId > 0) existing.scheduledAppointmentProductsIds.push(sapId);
      } else {
        byType.set(ptId, {
          quoteItemCostId: Number(pt.productID) || 0,
          quantity: 1,
          productTypeName: pt.productTypeName,
          scheduledAppointmentProductsIds: sapId > 0 ? [sapId] : [],
        });
      }
    }
    for (const slot of pt.purchaseOthers ?? []) {
      const existing = byType.get(ptId);
      const sapId = slot.scheduledAppointmentProductsID ? Number(slot.scheduledAppointmentProductsID) : 0;
      if (existing) {
        existing.quantity += 1;
        if (sapId > 0) existing.scheduledAppointmentProductsIds.push(sapId);
      } else {
        byType.set(ptId, {
          quoteItemCostId: Number(pt.productID) || 0,
          quantity: 1,
          productTypeName: pt.productTypeName,
          scheduledAppointmentProductsIds: sapId > 0 ? [sapId] : [],
        });
      }
    }
  }

  return Array.from(byType.values()).map((group) => ({
    quoteItemCostId: group.quoteItemCostId,
    quantity: group.quantity,
    productTypeName: group.productTypeName,
    scheduledAppointmentProductsIds: group.scheduledAppointmentProductsIds,
  }));
}

/**
 * P7 — First-Appointment details panel. Reproduces the legacy
 * `[data-firstappt-details]` block in `scheduler-appt-details.php`
 * (L1518-L1563): a soft-bordered container with a green message slot
 * (`[data-firstmsg]` — left empty in legacy unless a future hook
 * populates it), the additional duration applied when the box is
 * checked (`[data-firstduration]`), and the first-time notes
 * (`[data-firstnotes]`, falling back to a fixed phrase when the addon
 * row carries no notes). Visibility is controlled by the parent —
 * legacy ties it to the `firstAppointment` checkbox state and we do
 * the same. Reference: docs/audit/appt-details-parity.md §1.8 +
 * §5.C.12.
 */
function FirstApptPanel({
  defaults,
  showHeader,
}: {
  defaults: AppointmentTypePatientDefaults;
  showHeader?: boolean;
}) {
  const trimmedNotes = (defaults.firstAppointmentTypeNotes ?? '').trim();
  const hasDuration = (defaults.firstAppointmentTypeDuration ?? 0) > 0;
  const hasNotes = trimmedNotes !== '';

  const TECH_KEYWORDS = ['optims', 'tech credits', 'tech credit'];
  const useApptType =
    defaults.productTypes.length === 0 ||
    defaults.productTypes.some((pt) =>
      TECH_KEYWORDS.includes((pt.productTypeName ?? '').toLowerCase()),
    );

  const productName =
    !useApptType && defaults.productTypes.length > 0
      ? defaults.productTypes[0].productTypeName
      : '';

  const firstTimeReason = useApptType
    ? `This is the patient\u2019s first \u201C${defaults.appointmentTypeName}\u201D appointment`
    : productName
      ? `This is the patient\u2019s first \u201C${productName}\u201D appointment`
      : `This is the patient\u2019s first \u201C${defaults.appointmentTypeName}\u201D appointment`;

  return (
    <div className="col-span-2 md:col-span-4 mt-2">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-slate-700 space-y-2">
        {showHeader ? (
          <div className="text-sm font-semibold text-blue-700">
            First Time Details
          </div>
        ) : null}
        {firstTimeReason ? (
          <div className="text-sm font-medium text-blue-800">{firstTimeReason}</div>
        ) : null}
        {hasDuration ? (
          <div className="text-sm">
            Additional Duration:{' '}
            <span className="font-bold">{defaults.firstAppointmentTypeDuration}</span> min(s)
          </div>
        ) : null}
        {hasNotes ? (
          <div>
            <div className="font-semibold text-slate-800">Notes:</div>
            <div
              className="mt-0.5 prose prose-sm max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: trimmedNotes }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface UnifiedAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired on any successful mutation (create / update / cancel / delete / copy). */
  onSaved?: (appointment: Appointment | null) => void;
  /** When provided, modal runs in view/edit mode. Absent → create mode. */
  appointment?: Appointment;
  defaultDate?: string;
  defaultStartTime?: string;
  defaultPatientId?: number;
  defaultPatient?: PatientSummary | null;
  defaultResourceIds?: number[];
  /** Prefill patient plan in create mode. */
  defaultPatientPlanId?: number;
  // Convert-Block-to-Appointment seed plumbing (legacy `convertBlockToAppt`,
  // calendar-test.php L6714-L6733). All optional; only used in create mode.
  defaultEndTime?: string;
  defaultDuration?: number;
  defaultName?: string;
  defaultReason?: string;
  defaultFacilityId?: number;
  defaultAppointmentTypeId?: number;
  defaultAppointmentTypeGroupId?: number;
  /**
   * Privacy-mode toggle from `users_filters_sched.privacyModeToggle`. Mirrors
   * legacy `scheduler-appt-details.php` L682, which renders the patient name
   * with `visibility:hidden` when the active scheduler preference enables
   * privacy. Threaded down from `SchedulePage` so the modal honors the same
   * setting the calendar grid does. Reference: docs/audit/appt-details-parity.md
   * §1.1 Privacy + P5.
   */
  privacyMode?: boolean;

  /**
   * P8 — "Find Available Time" toolbar callback. Mirrors the legacy
   * `loadNextAvailPage(...)` action wired off `existingApptAvail = 1`
   * (`scheduler-appt-details.php` L760). The parent should open the
   * Next-Available view pre-seeded with the current appointment's type /
   * group / facility so the user can pick a free slot and return to the
   * modal with new date/time defaults. The button is hidden when this
   * callback is not provided so existing call-sites don't see a dead
   * button. Reference: docs/audit/appt-details-parity.md §1.2 Find
   * Available Time.
   */
  onFindAvailableTime?: (seed: AppointmentNextAvailableSeed) => void;
}

/**
 * Snapshot built when the user clicks Copy. Carries every legacy
 * `conv*` JS-global from `appointment-schedule.js` L8847 so the parent
 * can re-open the create modal in the exact same state legacy would.
 *
 * The shape intentionally matches `BlockToAppointmentSeed['seed']` from
 * `appointments.service.ts` so the parent (`SchedulePage`) can route both
 * the Copy and the Convert-Block flows through the same `convertSeed`
 * state. Times are full ISO `YYYY-MM-DDTHH:MM:SS` strings (or `null`)
 * exactly like the back-end's block-to-appt converter, so the existing
 * `convertSeed.startTime.slice(0, 10)` / `.slice(11, 16)` parsing in
 * `SchedulePage` keeps working with no change.
 */
export interface AppointmentCopySeed {
  title: string;
  startTime: string | null;
  endTime: string | null;
  duration: number;
  facilityId: number;
  appointmentTypeId: number;
  appointmentTypeGroupId: number;
  resourceIds: number[];
  notes: string;
  patientId: number;
  planId: number;
  /** Full patient summary so the create modal can render the patient
   *  pill without re-fetching. Mirrors legacy `convPatientName`. */
  patient: PatientSummary | null;
}

/**
 * Snapshot for the legacy "Find Available Time" action — only the
 * fields the next-available search needs. Carries through whatever the
 * user has selected in the modal so we don't reset their context.
 */
export interface AppointmentNextAvailableSeed {
  appointmentTypeId?: number;
  appointmentTypeGroupId?: number;
  facilityId?: number;
  duration?: number;
  resourceIds: number[];
}

export function UnifiedAppointmentModal(props: UnifiedAppointmentModalProps) {
  // When closed, unmount the inner component entirely so ALL state resets on
  // next open — exactly like the legacy PHP which loaded a fresh page each time.
  if (!props.isOpen) return null;
  return <UnifiedAppointmentModalInner {...props} />;
}

function UnifiedAppointmentModalInner({
  isOpen,
  onClose,
  onSaved,
  appointment,
  defaultDate,
  defaultStartTime,
  defaultPatientId,
  defaultPatient,
  defaultResourceIds,
  defaultPatientPlanId,
  defaultEndTime,
  defaultDuration,
  defaultName,
  defaultReason,
  defaultFacilityId,
  defaultAppointmentTypeId,
  defaultAppointmentTypeGroupId,
  privacyMode = false,
  onFindAvailableTime,
}: UnifiedAppointmentModalProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const visitPatient = usePatientHistoryStore((s) => s.visit);
  const [copyMode, setCopyMode] = useState(false);
  const copySnapshotRef = useRef<AppointmentCopySeed | null>(null);
  const isEditMode = Boolean(appointment) && !copyMode;
  const appointmentId = copyMode ? undefined : appointment?.appointmentId;
  const globalFacilityId = useGlobalFacilityStore((s) => s.selectedFacilityIds[0] ?? null);
  // Legacy `scheduler-appt-details.php` L751 only renders the Delete
  // toolbar button for users with `user_level > 2`. Parity matters here
  // because the server already enforces it (`AppointmentExtrasController::
  // hardDelete` L979) — but legacy hides the control entirely so users
  // who can't delete don't see a dead button. Reference:
  // docs/audit/appt-details-parity.md §1.2 Delete + P8.
  const currentUserLevel = useAuthStore((s) => s.user?.userLevel ?? 0);
  const canDeleteAppointment = currentUserLevel > 3;

  // ─── Reference data (cached via React Query — survives modal unmount) ─────
  const REF_STALE = 10 * 60_000;
  const { data: types = [], isLoading: loadingTypes } = useQuery({
    queryKey: ['appt-ref-types'],
    queryFn: () => appointmentsService.getAppointmentTypes(),
    staleTime: REF_STALE,
  });
  const { data: resources = [], isLoading: loadingResources } = useQuery({
    queryKey: ['appt-ref-resources'],
    queryFn: () => appointmentsService.getResources(),
    staleTime: REF_STALE,
  });
  const { data: facilities = [], isLoading: loadingFacilities } = useQuery({
    queryKey: ['appt-ref-facilities'],
    queryFn: listFacilities,
    staleTime: REF_STALE,
  });
  const { data: cancelReasons = [] } = useQuery({
    queryKey: ['appt-ref-cancel-reasons'],
    queryFn: () => appointmentsService.listAppointmentCancelReasons(),
    staleTime: REF_STALE,
  });
  const { data: calSettings, isLoading: loadingCalSettings } = useQuery({
    queryKey: ['calendar-settings'],
    queryFn: getCalendarSettings,
    staleTime: REF_STALE,
  });
  const loadingRefs = loadingTypes || loadingResources || loadingFacilities || loadingCalSettings;
  const accountIanaTimezone = calSettings?.accountIanaTimezone ?? PACIFIC_TZ;

  // ─── Controlled pieces outside RHF ────────────────────────────────────────
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(null);

  // Patient id for the linked-notes UI. `selectedPatient` wins so a patient swapped on
  // an open modal is respected, but fall back to the appointment's own id:
  // buildSelectedPatient() returns null when the display name is blank (L292-294) and
  // Appointment.patientName is nullable, which would otherwise hide the whole
  // linked-notes UI for an appointment that has a patient attached.
  const notesPatientId = selectedPatient?.patientId ?? appointment?.patientId ?? undefined;
  const canLinkNotes = Boolean(isEditMode && appointmentId && notesPatientId);

  // ─── Linked clinical notes ────────────────────────────────────────────────
  // Owned here rather than in AppointmentLinkedNotes because the count drives the
  // Notes section header badge and the auto-expand effect below — both of which are
  // this component's concerns. The child invalidates the same key.
  const { data: linkedNotes = [], isLoading: linkedNotesLoading } = useQuery({
    queryKey: ['appt-linked-notes', appointmentId, notesPatientId],
    queryFn: () => appointmentsService.getAppointmentNotes(appointmentId!),
    enabled: canLinkNotes,
    staleTime: 30_000,
  });

  // ─── Patient tasks: past due + due today human tasks ──────────────────────
  const { data: patientTasksRaw = [] } = useQuery({
    queryKey: ['appt-patient-tasks', selectedPatient?.patientId],
    queryFn: () =>
      listTasks({ patientId: selectedPatient!.patientId, flTasks: 1, pageSize: 200 })
        .then((r) => r.data ?? []),
    enabled: !!selectedPatient?.patientId,
    staleTime: 30_000,
  });

  const humanTasksDueSoon = useMemo(() => {
    return patientTasksRaw
      .filter((t: TaskRow) =>
        // Human tasks only. Exclude anything the task engine fires on its own:
        //   - autoTaskID 1-9 (Email/SMS/Opt-out/Tag/Survey/Stage) auto-fire.
        //     `?? 0` treats a missing field (0=Human) as human, so this stays
        //     backwards-compatible if the API hasn't shipped autoTaskID yet.
        //   - assignedToID 'ftp' is the Prospera / Follow That Patient system
        //     assignee (renders as "Prospera"); these fire automatically too.
        (t.autoTaskID ?? 0) === 0 &&
        t.assignedToID !== 'ftp' &&
        !!t.assignedToName?.trim() &&
        (t.derivedStatus === 'Past Due' || t.derivedStatus === 'Due'),
      )
      .sort((a: TaskRow, b: TaskRow) => {
        const da = a.lastDue ?? a.dueDate ?? '';
        const db = b.lastDue ?? b.dueDate ?? '';
        return da.localeCompare(db);
      });
  }, [patientTasksRaw]);
  const [changingPatient, setChangingPatient] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const statusMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!statusMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusMenuOpen]);
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<number>>(new Set());
  // AR-4: local "drafts" for rows the user has started (picked a type) but
  // hasn't yet finished by choosing a resource. Once the resource is picked
  // the row commits into `selectedResourceIds` and the draft is removed.
  const [rowDrafts, setRowDrafts] = useState<
    Array<{ key: string; resourceType: Resource['resourceType'] }>
  >([]);

  // ─── Status timestamps (local state so Now/Clear is instant) ──────────────
  const [contactAttemptedAt, setContactAttemptedAt] = useState<string | null>(null);
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null);
  const [confirmedFlag, setConfirmedFlag] = useState(false);
  const [confirmedContactMethod, setConfirmedContactMethod] = useState<string>('');
  const [checkInAt, setCheckInAt] = useState<string | null>(null);
  const [checkBackAt, setCheckBackAt] = useState<string | null>(null);
  const [checkOutAt, setCheckOutAt] = useState<string | null>(null);
  const [cancelledAt, setCancelledAt] = useState<string | null>(null);
  const [cancelledFlag, setCancelledFlag] = useState(false);
  const [cancelledReason, setCancelledReason] = useState('');
  // `null` = nothing picked yet (shows the placeholder); `0` = the "Other"
  // option, which requires free-text details.
  const [cancelReasonID, setCancelReasonID] = useState<number | null>(null);
  const [noShowFlag, setNoShowFlag] = useState(false);

  // ─── Unsaved changes tracking ────────────────────────────────────────────
  const [nonFormDirty, setNonFormDirty] = useState(false);
  const initialSnapshotRef = useRef<string>('');
  const formReadyRef = useRef(false);

  // ─── Lazy-loaded data ─────────────────────────────────────────────────────
  const [shortcodes, setShortcodes] = useState<AppointmentShortcode[]>([]);
  const [shortcodeValues, setShortcodeValues] = useState<Record<string, string>>({});
  const [addProductsOpen, setAddProductsOpen] = useState(false);

  // New PatientProductTypes-based product data (replaces old product data for display)
  const [newProductData, setNewProductData] = useState<NewApptProductsResponse | null>(null);
  const [newProductsLoading, setNewProductsLoading] = useState(false);
  const [newProductsError, setNewProductsError] = useState<string | null>(null);
  const productDataRef = useRef<NewApptProductsResponse | null>(null);
  // Only the latest products request may land; an older one finishing late
  // would show (and sync the comp toggle to) stale products.
  const productsRequestRef = useRef(0);
  // True while the Comp Products toggle's own request runs. A products reload
  // landing in that window predates the comp, so the toggle is not synced to it.
  const compRequestInFlightRef = useRef(false);
  // Manual required-set switch. null = follow the server default (membership /
  // whatever is already attached); set only when the user picks an option.
  const [productModeOverride, setProductModeOverride] = useState<ProductMode | null>(null);
  const [patientInventory, setPatientInventory] = useState<Record<string, unknown> | null>(null);
  const [history, setHistory] = useState<AppointmentHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ─── Conflict / availability strip ────────────────────────────────────────
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [warnings, setWarnings] = useState<ConflictInfo[]>([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  // AR-2: per-resource conflict map so the AR-4 ResourceRowList can render a
  // row-level indicator. Legacy appointment-schedule.js L6674 runs
  // conflictCheck() on every resource independently.
  const [resourceConflicts, setResourceConflicts] = useState<Record<number, ConflictInfo[]>>({});

  // ─── Product alert state ─────────────────────────────────────────────────
  const [productAlertCost, setProductAlertCost] = useState<number | null>(null);

  // ─── Unsaved changes confirm dialog ─────────────────────────────────────
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  // ─── Delete confirm dialog ──────────────────────────────────────────────
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ─── Handoff modal state ──────────────────────────────────────────────────
  const [chargeOpen, setChargeOpen] = useState(false);
  const chargePrefill = useMemo(() => buildPrefillItems(newProductData), [newProductData]);

  // ─── Plan picker / New Plan state (legacy "Find Plan" / "New Plan") ──────
  // Mirrors the inline `selectPatientButton` (`scheduler-appt-details.php`
  // L776) and `addPlanButton` (L768) controls. Find Plan opens an inline
  // picker over the patient's plans (`/patients/{id}/plans`); New Plan opens
  // `PlanCreateModal` seeded with the attached patient. Reference:
  // docs/audit/appt-details-parity.md §1.1 + §5.C.11 + P5.
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [planPickerOptions, setPlanPickerOptions] = useState<IntakePatientPlan[]>([]);
  const [planPickerLoading, setPlanPickerLoading] = useState(false);

  // "Add Patient" beside the patient search — the intake form in a nested modal,
  // for when the person being booked isn't in the system yet.
  const [addPatientOpen, setAddPatientOpen] = useState(false);

  // ─── First-Appt panel state (P7) ──────────────────────────────────────────
  // Holds the per-(type, patient) defaults that drive the legacy
  // `[data-firstappt-details]` panel (legacy markup
  // `scheduler-appt-details.php` L1518-L1563) and the auto-check logic in
  // `appointment-schedule.js::loadApptDefaultDetails` (L820-L932). Refreshed
  // every time the selected appointment type or patient changes; cleared
  // whenever no type is selected. Reference: docs/audit/appt-details-parity.md
  // §1.8 + §5.C.12.
  const [firstApptDefaults, setFirstApptDefaults] = useState<AppointmentTypePatientDefaults | null>(
    null,
  );

  // ─── Panel collapse state ─────────────────────────────────────────────────
  const [collapsed, setCollapsed] = useState({
    validation: true,
    details: false,
    status: false,
    notes: true,
    resources: true,
    tasks: true,
    products: false,
    patientInventory: true,
    shortcodes: true,
    history: true,
  });

  const toggleCollapsed = useCallback((key: keyof typeof collapsed) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ─── Submission state ─────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [saveOverlay, setSaveOverlay] = useState<'saving' | 'saved' | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showAutoSaveOverlay = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveOverlay('saving');
  }, []);

  const hideAutoSaveOverlay = useCallback(() => {
    setSaveOverlay('saved');
    autoSaveTimerRef.current = setTimeout(() => {
      setSaveOverlay(null);
      autoSaveTimerRef.current = null;
    }, 800);
  }, []);
  const [deleting, setDeleting] = useState(false);
  const [sendingConfirmation, setSendingConfirmation] = useState(false);

  // Stage-change confirmation and block warnings (scheduler_check_stage.php +
  // scheduler_check_blocks.php parity).
  const [stageRecommendation, setStageRecommendation] = useState<{
    targetStageId: number;
    targetStageName: string | null;
    triggerAction: string;
  } | null>(null);
  const [stageSavedId, setStageSavedId] = useState<number | null>(null);
  // The saved appointment whose onSaved is held back while the stage prompt is
  // open. Every create host closes this modal inside onSaved (and SchedulePage
  // closes the edit modal there too), so calling it before the prompt unmounted
  // the prompt with the modal and it never showed.
  const [stagePendingSaved, setStagePendingSaved] = useState<Appointment | null>(null);
  const [stageApplying, setStageApplying] = useState(false);
  const [blockWarnings, setBlockWarnings] = useState<ConflictInfo[]>([]);
  const [blockConflicts, setBlockConflicts] = useState<ConflictInfo[]>([]);

  // Field-level validation feedback shown above the form. Populated from
  // either client-side zod errors (via the form submit handler) or server
  // 422 responses; cleared the moment the user starts editing.
  const [validationBanner, setValidationBanner] = useState<{
    title: string;
    fields: Array<{ label: string; message: string }>;
    extra?: string[];
  } | null>(null);
  // The banner sits at the top of the scrolling body; a save rejected while
  // the user is scrolled down to the buttons would otherwise go unseen.
  const validationBannerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (validationBanner) {
      validationBannerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [validationBanner]);

  // ─── Form setup ───────────────────────────────────────────────────────────
  // Plan requirement (SCRUM-2180): everything the Plan field's rule needs
  // except the plan itself, refreshed every render below. The resolver reads
  // it so a missing plan blocks the save like any other field error.
  const planContextRef = useRef<Omit<PlanFieldInput, 'planId'> | null>(null);
  // True when the form's plan was picked in the Plan field (not handed in by
  // the caller or already attached) — only such a plan is dropped when the
  // type stops needing one.
  const planChosenInPickerRef = useRef(false);
  // True when the form's plan is the patient's only plan, chosen for them
  // (autoPickedPlan). Dropped with the type like a picked plan.
  const planAutoPickedRef = useRef(false);
  // The patient whose Plan field the user emptied, so their only plan is not
  // put straight back.
  const planClearedForPatientRef = useRef<number | null>(null);
  const formResolver = useMemo(
    () =>
      withPlanRequirement(zodResolver(schema), (values: FormValues) =>
        planContextRef.current
          ? planFieldView({ ...planContextRef.current, planId: values.patientPlanId }).error
          : null,
      ),
    [],
  );
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    formState: { errors, isDirty: formIsDirty },
  } = useForm<FormValues>({
    resolver: formResolver,
    defaultValues: {
      date: defaultDate ?? '',
      startTime: defaultStartTime ?? '',
      duration: 30,
      waitList: false,
      firstAppointment: false,
      compAppointment: false,
    },
  });

  const hasUnsavedChanges = formReadyRef.current && (formIsDirty || nonFormDirty);

  // Every write of the form's plan outside a reset goes through here, so the
  // "picked in the Plan field" flag always describes the plan actually held.
  // "No plan" is written as 0, not undefined: `watch` answers an undefined
  // field with the value from the last reset, which in edit mode is the saved
  // plan. The schema turns 0 into "no plan" before the save.
  const setFormPlan = useCallback(
    (sel: PlanSelection, opts?: { shouldDirty?: boolean }) => {
      planChosenInPickerRef.current = sel.chosenInPicker;
      planAutoPickedRef.current = Boolean(sel.autoPicked);
      setValue('patientPlanId', sel.planId ?? 0, opts);
      clearErrors('patientPlanId');
    },
    [setValue, clearErrors],
  );

  // ─── Populate form on open ────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setCopyMode(false);
      copySnapshotRef.current = null;
      return;
    }
    setValidationBanner(null);
    setBlockConflicts([]);
    setBlockWarnings([]);
    planChosenInPickerRef.current = false;
    planAutoPickedRef.current = false;
    planClearedForPatientRef.current = null;
    if (appointment && !copyMode) {
      // API returns times as UTC; convert to the facility/account display TZ.
      const editFacTz = appointment.facilityId
        ? (facilities.find((f) => f.facilityId === appointment.facilityId)?.ianaTimezone
          ?? accountIanaTimezone)
        : accountIanaTimezone;
      const displayStart = utcToWallClock(appointment.startTime, editFacTz)
        ?? { date: toDate(appointment.startTime), time: toTime(appointment.startTime) };
      const displayEnd = utcToWallClock(appointment.endTime, editFacTz)
        ?? { date: toDate(appointment.endTime), time: toTime(appointment.endTime) };

      reset({
        name: appointment.name ?? '',
        patientId: appointment.patientId ?? undefined,
        patientPlanId: appointment.patientPlanId ?? undefined,
        appointmentTypeGroupId: appointment.appointmentTypeGroupId ?? undefined,
        appointmentTypeId: appointment.appointmentTypeId ?? undefined,
        facilityId: appointment.facilityId ?? undefined,
        date: displayStart.date,
        startTime: displayStart.time,
        endTime: displayEnd.time,
        duration: appointment.duration ?? 30,
        reason: appointment.reason ?? '',
        waitList: appointment.waitList ?? false,
        firstAppointment: appointment.firstAppointment ?? false,
        compAppointment: appointment.compAppointment ?? false,
      });
      savedFormFieldsRef.current = {
        typeId: appointment.appointmentTypeId ?? null,
        date: displayStart.date,
        startTime: displayStart.time,
      };
      setSelectedPatient(
        buildSelectedPatient({
          patientId: appointment.patientId,
          displayName: appointment.patientName,
          displayId: appointment.patientDisplayId,
          dateOfBirth: appointment.patientDateOfBirth,
        }),
      );
      setSelectedResourceIds(new Set(appointment.resources.map((r) => r.resourceId)));
      setRowDrafts([]);
      setContactAttemptedAt(utcIsoToPacificIso(appointment.contactAttemptedAt));
      setConfirmedAt(utcIsoToPacificIso(appointment.confirmedDate));
      setConfirmedFlag(appointment.confirmed ?? false);
      setConfirmedContactMethod(appointment.confirmedContactMethod ?? '');
      setCheckInAt(utcIsoToPacificIso(appointment.checkedInAt));
      setCheckBackAt(utcIsoToPacificIso(appointment.checkedBackAt));
      setCheckOutAt(utcIsoToPacificIso(appointment.checkedOutAt));
      setCancelledAt(utcIsoToPacificIso(appointment.cancelledDate));
      const isCancelled = appointment.status === 'cancelled';
      setCancelledFlag(isCancelled);
      // Reason state belongs to a cancellation — ignore whatever the column
      // holds for a live appointment (older records kept the text of a
      // since-reverted cancellation). Only keep "Other" (0) pre-selected when
      // there is actual detail text to show; otherwise start unselected so a
      // reason must be chosen.
      const loadedReason = isCancelled ? normalizeCancelReason(appointment.cancelledReason) : '';
      const loadedReasonID = isCancelled ? (appointment.cancelReasonID ?? 0) : 0;
      setCancelledReason(loadedReason);
      setCancelReasonID(loadedReasonID > 0 ? loadedReasonID : (loadedReason ? 0 : null));
      setNoShowFlag(appointment.noShow ?? false);
    } else {
      const snap = copySnapshotRef.current;
      reset({
        name: snap?.title ?? defaultName ?? '',
        reason: snap?.notes ?? defaultReason ?? '',
        date: snap?.startTime?.slice(0, 10) ?? (defaultDate || getNowPacific().slice(0, 10)),
        startTime: snap?.startTime?.slice(11, 16) ?? defaultStartTime ?? '',
        endTime: snap?.endTime?.slice(11, 16) ?? defaultEndTime ?? '',
        duration: (snap?.duration && snap.duration > 0 ? snap.duration : null) ?? (defaultDuration && defaultDuration > 0 ? defaultDuration : 30),
        appointmentTypeId: snap?.appointmentTypeId || defaultAppointmentTypeId,
        appointmentTypeGroupId: snap?.appointmentTypeGroupId || defaultAppointmentTypeGroupId,
        waitList: false,
        firstAppointment: false,
        compAppointment: false,
        patientId: snap?.patientId || defaultPatientId,
        patientPlanId: snap?.planId || defaultPatientPlanId,
        facilityId: snap?.facilityId || (defaultFacilityId ?? globalFacilityId ?? undefined),
      });
      setSelectedPatient(snap?.patient ?? defaultPatient ?? buildSelectedPatient({ patientId: (snap?.patientId || defaultPatientId) }));
      setSelectedResourceIds(new Set(snap?.resourceIds ?? defaultResourceIds ?? []));
      setRowDrafts([]);
      setContactAttemptedAt(null);
      setConfirmedAt(null);
      setConfirmedFlag(false);
      setConfirmedContactMethod('');
      setCheckInAt(null);
      setCheckBackAt(null);
      setCheckOutAt(null);
      setCancelledAt(null);
      setCancelledFlag(false);
      setCancelledReason('');
      setCancelReasonID(null);
      setNoShowFlag(false);
    }
    setShortcodes([]);
    setShortcodeValues({});
    setAddProductsOpen(false);
    setNewProductData(null);
    productDataRef.current = null;
    setPatientInventory(null);
    setNewProductsLoading(false);
    setNewProductsError(null);
    setConflicts([]);
    setWarnings([]);
    setCheckingConflicts(false);
    setResourceConflicts({});
    setChargeOpen(false);
    setChangingPatient(false);
    setStatusMenuOpen(false);
    setPlanPickerOpen(false);
    setNewPlanOpen(false);
    setAddPatientOpen(false);
    setPlanPickerOptions([]);
    setPlanPickerLoading(false);
    setFirstApptDefaults(null);
    setCollapsed({
      validation: true,
      details: false,
      status: false,
      notes: true,
      resources: true,
      tasks: true,
      products: false,
      patientInventory: true,
      shortcodes: true,
      history: true,
    });
    notesAutoExpandedRef.current = false;
    setNonFormDirty(false);
    initialSnapshotRef.current = '';
    formReadyRef.current = false;
    requestAnimationFrame(() => { formReadyRef.current = true; });
    setSubmitting(false);
    setSaveOverlay(null);
    if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    setDeleting(false);
    setSendingConfirmation(false);
    setStageRecommendation(null);
    setStageSavedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    appointment,
    copyMode,
    defaultDate,
    defaultStartTime,
    defaultEndTime,
    defaultDuration,
    defaultName,
    defaultReason,
    defaultFacilityId,
    globalFacilityId,
    defaultAppointmentTypeId,
    defaultAppointmentTypeGroupId,
    defaultPatientId,
    defaultPatient,
    defaultPatientPlanId,
    defaultResourceIds,
  ]);

  // ─── Non-form dirty tracking ─────────────────────────────────────────────
  // Snapshot non-form state after reset; mark dirty when it diverges.
  useEffect(() => {
    if (!isOpen || !formReadyRef.current) return;
    const snap = JSON.stringify({
      contactAttemptedAt, confirmedAt, confirmedFlag, confirmedContactMethod,
      checkInAt, checkBackAt, checkOutAt, cancelledAt, cancelledFlag,
      cancelledReason, cancelReasonID, noShowFlag, resources: [...selectedResourceIds].sort(),
    });
    if (!initialSnapshotRef.current) {
      initialSnapshotRef.current = snap;
    } else if (snap !== initialSnapshotRef.current) {
      setNonFormDirty(true);
    }
  });

  // Reset snapshot when modal opens fresh
  useEffect(() => {
    if (!isOpen) {
      initialSnapshotRef.current = '';
      setNonFormDirty(false);
      formReadyRef.current = false;
      savedFormFieldsRef.current = null;
    }
  }, [isOpen]);

  // Browser tab close / refresh guard
  useEffect(() => {
    if (!isOpen || !hasUnsavedChanges) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isOpen, hasUnsavedChanges]);

  // Browser back/forward navigation guard
  useEffect(() => {
    if (!isOpen || !hasUnsavedChanges) return;
    const handlePopState = () => {
      const leave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!leave) {
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, hasUnsavedChanges]);

  // ─── Correct edit-mode times to the FACILITY timezone as soon as it is
  // resolvable. The form-populate effect above runs on open with
  // accountIanaTimezone defaulted to Pacific (facilities may not be loaded yet),
  // so the initial value can be in the account/Pacific zone; we must reproject it
  // to the appointment's facility zone.
  //
  // Gate ONLY on the facility timezone being known — the facilities list for a
  // facility appointment (which every appointment has), or calSettings for the
  // rare facility-less one. Do NOT gate on `loadingRefs`, which also waits on the
  // appointment-type and resource queries: if one of those lags or errors, the
  // correction would never run and the time would stay stuck in the Pacific zone
  // under a facility (e.g. EDT) label — the same appointment then renders
  // differently between sessions. Reproject from the authoritative UTC source;
  // runs once, when the facility tz first resolves. No PACIFIC_TZ short-circuit:
  // a genuinely-Pacific facility just reprojects UTC→Pacific (a correct no-op).
  useEffect(() => {
    if (!isOpen || !appointment) return;
    if (editTimeCorrectedRef.current) return;
    const editFacTz = appointment.facilityId
      ? (loadingFacilities
        ? undefined
        : (facilities.find((f) => f.facilityId === appointment.facilityId)?.ianaTimezone
          ?? accountIanaTimezone))
      : (loadingCalSettings ? undefined : accountIanaTimezone);
    if (!editFacTz) return; // facility tz not resolved yet — retry when it is.
    editTimeCorrectedRef.current = true;
    const displayStart = utcToWallClock(appointment.startTime, editFacTz)
      ?? { date: toDate(appointment.startTime), time: toTime(appointment.startTime) };
    const displayEnd = utcToWallClock(appointment.endTime, editFacTz)
      ?? { date: toDate(appointment.endTime), time: toTime(appointment.endTime) };
    setValue('date', displayStart.date);
    setValue('startTime', displayStart.time);
    setValue('endTime', displayEnd.time);
    savedFormFieldsRef.current = {
      typeId: appointment.appointmentTypeId ?? null,
      date: displayStart.date,
      startTime: displayStart.time,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appointment, facilities, accountIanaTimezone, loadingFacilities, loadingCalSettings]);

  // ─── Load appointment-scoped data when editing ───────────────────────────
  const loadHistory = useCallback(() => {
    if (!appointmentId) return;
    setHistoryLoading(true);
    appointmentsService
      .getAppointmentHistory(appointmentId)
      .then(setHistory)
      .catch((err) => {
        console.error('[AppointmentHistory] Failed to load history for', appointmentId, err);
        setHistory([]);
      })
      .finally(() => setHistoryLoading(false));
  }, [appointmentId]);

  const loadShortcodes = useCallback(() => {
    if (!appointmentId) return;
    appointmentsService
      .getAppointmentShortcodes(appointmentId)
      .then((rows) => {
        setShortcodes(rows);
        const seed: Record<string, string> = {};
        for (const row of rows) {
          seed[String(row.shortcodeId)] = row.contentText ?? '';
        }
        setShortcodeValues(seed);
      })
      .catch(() => setShortcodes([]));
  }, [appointmentId]);

  // Reads `appointmentTypeId` from the form state; when it changes (or the
  // patient changes) we re-fetch the buckets so the panel reflects the
  // chosen template even before the appointment is saved. Mirrors legacy
  // `setApptProductTypes()` rebuild on appointment-type change.
  const previewTypeId = (watch('appointmentTypeId') as number | null | undefined) ?? null;
  const previewPatientId = selectedPatient?.patientId ?? null;
  // P6 — comp-toggle live refresh: watch the `compAppointment` checkbox and
  // forward its current value on every products fetch so the grid reflects
  // the toggle without forcing a save first. Mirrors legacy
  // `appointment-schedule.js` L2414–L2427 where `loadApptProductDefaults`
  // posts `apptComp` derived from the live `[appt-comp]:checked` state.
  const watchedCompAppointment = Boolean(watch('compAppointment'));

  // ─── Comp guard: disable comp toggle when type/date/time diverge from saved
  const savedFormFieldsRef = useRef<{ typeId: number | null; date: string; startTime: string } | null>(null);
  const watchedTypeIdForComp = watch('appointmentTypeId') as number | null | undefined;
  const watchedDateForComp = watch('date') as string;
  const watchedStartTimeForComp = watch('startTime') as string;

  const productFieldsChanged = useMemo(() => {
    if (!isEditMode || !savedFormFieldsRef.current) return false;
    const saved = savedFormFieldsRef.current;
    return (
      (watchedTypeIdForComp ?? null) !== saved.typeId ||
      watchedDateForComp !== saved.date ||
      watchedStartTimeForComp !== saved.startTime
    );
  }, [isEditMode, watchedTypeIdForComp, watchedDateForComp, watchedStartTimeForComp]);

  useEffect(() => {
    if (!isOpen) return;
    if (appointmentId) {
      loadHistory();
      loadShortcodes();
    }
  }, [isOpen, appointmentId, loadHistory, loadShortcodes]);


  // ─── Cascading selects / duration ────────────────────────────────────────
  const watchedDate = watch('date');
  const watchedStartTime = watch('startTime');
  const watchedDuration = watch('duration');
  const watchedFacilityId = watch('facilityId');
  const watchedGroupId = watch('appointmentTypeGroupId');
  const watchedTypeId = watch('appointmentTypeId');
  const watchedPatientId = watch('patientId');

  const { data: giFormData } = useQuery({
    queryKey: ['patient-forms-list', watchedPatientId, false],
    queryFn: () => listPatientForms(watchedPatientId!, false),
    enabled: !!watchedPatientId && watchedPatientId > 0,
    staleTime: 30_000,
  });
  const giFormConfigured = giFormData?.giFormConfigured ?? false;
  const giFormUpToDate = giFormData?.giFormUpToDate ?? false;

  // Display timezone: facility IANA when a facility is selected, otherwise
  // account default. Mirrors legacy `general-date-functions.php`
  // getFacilityTzone fallback chain.
  const displayTz = useMemo(() => {
    if (watchedFacilityId) {
      const facTz = facilities.find((f) => f.facilityId === watchedFacilityId)?.ianaTimezone;
      if (facTz) return facTz;
    }
    return accountIanaTimezone;
  }, [watchedFacilityId, facilities, accountIanaTimezone]);

  const tzAbbreviation = useMemo(() => {
    const refDate = watchedDate ? new Date(`${watchedDate}T12:00:00`) : undefined;
    return getTimezoneAbbreviation(displayTz, refDate);
  }, [displayTz, watchedDate]);
  const watchedPatientPlanId = watch('patientPlanId');

  // ─── Plan requirement (SCRUM-2180) ────────────────────────────────────────
  // A type set to "Patient & Plan" cannot be saved without one of the
  // patient's plans; the server answers a 422 on `patientPlanId`. The rules
  // live in ./appointmentPlanRequirement so they can be tested.
  const typeRequirement = useMemo(
    () => types.find((t) => t.appointmentTypeId === watchedTypeId)?.requiresPatientPlan ?? 0,
    [types, watchedTypeId],
  );
  // null = the saved type is not in the list (an inactive type), so its
  // setting is unknown here; see savedRowAlreadyMissesRequirement.
  const savedTypeRequirement = useMemo(() => {
    const savedTypeId = appointment?.appointmentTypeId;
    if (!savedTypeId) return 0;
    return types.find((t) => t.appointmentTypeId === savedTypeId)?.requiresPatientPlan ?? null;
  }, [types, appointment?.appointmentTypeId]);
  const planPatientId = watchedPatientId && watchedPatientId > 0 ? watchedPatientId : null;
  const needsPlanList = typeRequirement === 2 && planPatientId !== null;
  const {
    data: patientPlans,
    isLoading: loadingPatientPlans,
    isError: patientPlansFailed,
    isFetchedAfterMount: patientPlansFetchedHere,
    isFetching: fetchingPatientPlans,
    refetch: refetchPatientPlans,
  } = useQuery({
    queryKey: ['patient-plans', planPatientId],
    queryFn: () => getPlansByPatient(planPatientId!),
    enabled: needsPlanList,
    // Always asked again: the only-plan pick below must see a plan added since
    // another screen cached this patient's list.
    staleTime: 0,
  });
  const planContext: Omit<PlanFieldInput, 'planId'> = {
    requirement: typeRequirement,
    patientId: planPatientId,
    // The quick-links "Plan #…" pill stands in for the field while it still
    // shows the plan the form will save.
    attachedPlanShown: Boolean(
      appointment?.patientPlanId &&
        appointment.planDisplayId &&
        watchedPatientPlanId === appointment.patientPlanId,
    ),
    savedRowAlreadyMisses: savedRowAlreadyMissesRequirement({
      isEditMode,
      savedRequirement: savedTypeRequirement,
      savedPatientId: appointment?.patientId,
      savedPlanId: appointment?.patientPlanId,
    }),
    plans: needsPlanList ? patientPlans : undefined,
    loadFailed: needsPlanList && patientPlansFailed,
  };
  planContextRef.current = planContext;
  const planView = planFieldView({ ...planContext, planId: watchedPatientPlanId });
  const planOptions = useMemo(
    () => buildPlanOptions(patientPlans, watchedPatientPlanId),
    [patientPlans, watchedPatientPlanId],
  );

  // A plan picked here is dropped when the type stops needing one, so it is
  // not sent with the save. A plan the modal was opened with is kept.
  useEffect(() => {
    const current = getValues('patientPlanId') || undefined;
    const next = planAfterTypeChange(
      {
        planId: current,
        chosenInPicker: planChosenInPickerRef.current,
        autoPicked: planAutoPickedRef.current,
      },
      typeRequirement,
    );
    if (next.planId !== current) {
      setFormPlan(next, { shouldDirty: true });
    } else {
      planChosenInPickerRef.current = next.chosenInPicker;
      planAutoPickedRef.current = Boolean(next.autoPicked);
    }
  }, [typeRequirement, getValues, setFormPlan]);

  // A new appointment for a patient with exactly one plan gets that plan
  // (SCRUM-2180), judged only on a list fetched for this booking: a cached
  // list may lack a plan added since, and a failed fetch leaves that cached
  // list in place. A plan chosen this way is taken back when a later fetch
  // shows it is no longer the only one. Written without shouldDirty: the user
  // has changed nothing.
  const freshPlans =
    patientPlansFetchedHere && !fetchingPatientPlans && !patientPlansFailed ? planContext.plans : undefined;
  useEffect(() => {
    const held: PlanSelection = {
      planId: getValues('patientPlanId') || undefined,
      chosenInPicker: planChosenInPickerRef.current,
      autoPicked: planAutoPickedRef.current,
    };
    const kept = planAfterPlansRefresh(held, freshPlans);
    if (kept.planId !== held.planId) setFormPlan(kept);
    const next = autoPickedPlan({
      isEditMode,
      requirement: typeRequirement,
      patientId: planPatientId,
      planId: kept.planId,
      plans: freshPlans,
      clearedForPatientId: planClearedForPatientRef.current,
    });
    if (next) setFormPlan(next);
  }, [isEditMode, typeRequirement, planPatientId, freshPlans, watchedPatientPlanId, getValues, setFormPlan]);

  // Auto-compute end time from start + duration
  useEffect(() => {
    if (watchedStartTime && watchedDuration) {
      setValue('endTime', computeEndTime(watchedStartTime, watchedDuration));
      // A recompute always yields a consistent end time, so clear any stale
      // "end before start" error left over from a rejected manual pick.
      clearErrors('endTime');
    }
  }, [watchedStartTime, watchedDuration, setValue, clearErrors]);

  // Auto-fill title from type. Title autofill mirrors legacy
  // `calendar-test.php` L1303-L1304: when the title field is blank or still
  // the legacy placeholder ("Untitled Appointment"), copy in the selected
  // appointment type's name. Duration is handled below by the first-appt
  // panel logic so the legacy `setDefaultDuration` semantics
  // (`appointmentTypeDuration + firstAppointmentTypeDuration`) come from a
  // single source of truth.
  useEffect(() => {
    const type = types.find((t) => t.appointmentTypeId === watchedTypeId);
    if (!type) return;
    if (type.appointmentTypeName) {
      const currentName = (getValues('name') ?? '').trim();
      if (currentName === '' || currentName === 'Untitled Appointment') {
        setValue('name', type.appointmentTypeName, { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [watchedTypeId, types, setValue, getValues]);

  // Pacific date/time for the products API. When the user changes date/time
  // in edit mode, convert form values (display TZ) back to Pacific so the
  // backend receives correct dates for availability filtering.
  const { productsPacificDate, productsPacificTime } = useMemo(() => {
    if (!appointment) {
      // Create mode: form values may be in display TZ
      const d = watchedDate ?? '';
      const t = watchedStartTime ?? '';
      if (!d || !t || displayTz === PACIFIC_TZ) return { productsPacificDate: d, productsPacificTime: t };
      const pac = reprojectDateTime(d, t, displayTz, PACIFIC_TZ);
      return { productsPacificDate: pac.date, productsPacificTime: pac.time };
    }
    if (!productFieldsChanged) {
      // Edit mode, no changes: convert API's UTC startTime to Pacific
      const pac = utcToWallClock(appointment.startTime, PACIFIC_TZ);
      return pac
        ? { productsPacificDate: pac.date, productsPacificTime: pac.time }
        : { productsPacificDate: toDate(appointment.startTime), productsPacificTime: toTime(appointment.startTime) };
    }
    // Edit mode, user changed fields: convert current form values to Pacific
    const d = watchedDateForComp ?? '';
    const t = watchedStartTimeForComp ?? '';
    if (!d || !t || displayTz === PACIFIC_TZ) return { productsPacificDate: d, productsPacificTime: t };
    const pac = reprojectDateTime(d, t, displayTz, PACIFIC_TZ);
    return { productsPacificDate: pac.date, productsPacificTime: pac.time };
  }, [appointment, productFieldsChanged, watchedDate, watchedStartTime, watchedDateForComp, watchedStartTimeForComp, displayTz]);

  const loadNewProducts = useCallback(() => {
    const request = ++productsRequestRef.current;
    if (!previewPatientId || !previewTypeId) {
      setNewProductData(null);
      productDataRef.current = null;
      return;
    }
    setNewProductsLoading(true);
    setNewProductsError(null);
    const apptIDStr = appointmentId ? String(appointmentId) : 'NEW';
    const facilityIdStr = watchedFacilityId ? String(watchedFacilityId) : '';
    const startDateStr = productsPacificDate && productsPacificTime
      ? `${productsPacificDate} ${productsPacificTime}:00`
      : '';

    fetchNewAppointmentProducts(String(previewPatientId), {
      appointmentTypeID: String(previewTypeId),
      apptID: apptIDStr,
      apptStartDate: startDateStr,
      apptDate: productsPacificDate,
      facilityID: facilityIdStr,
      apptComp: watchedCompAppointment ? '1' : '',
      ...(productModeOverride ? { productMode: productModeOverride } : {}),
    })
      .then((res) => {
        if (request !== productsRequestRef.current) return;
        const d = res?.data ?? null;
        setNewProductData(d);
        productDataRef.current = d;
        if (d?.patientInventory) {
          setPatientInventory(d.patientInventory as Record<string, unknown>);
        }
      })
      .catch((err) => {
        if (request !== productsRequestRef.current) return;
        const msg =
          err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          'Unknown error';
        console.error('loadNewProducts failed:', msg, err?.response?.data, err);
        setNewProductsError(msg);
        setNewProductData(null);
        productDataRef.current = null;
      })
      .finally(() => {
        if (request === productsRequestRef.current) setNewProductsLoading(false);
      });
  }, [appointmentId, previewPatientId, previewTypeId, productsPacificDate, productsPacificTime, watchedFacilityId, watchedCompAppointment, productModeOverride]);

  useEffect(() => {
    if (!isOpen) return;
    loadNewProducts();
  }, [isOpen, loadNewProducts]);

  // Reset the manual required-set choice whenever the patient or type changes,
  // so the membership default re-applies for the new context.
  useEffect(() => {
    setProductModeOverride(null);
  }, [previewPatientId, previewTypeId]);

  useEffect(() => {
    if (!isOpen || !previewPatientId) {
      setPatientInventory(null);
      return;
    }
    if (previewTypeId) return;
    let cancelled = false;
    getPatientProductTypes(String(previewPatientId))
      .then((res) => {
        if (cancelled) return;
        const types = res?.data?.['product-types'] ?? {};
        setPatientInventory(types as Record<string, unknown>);
      })
      .catch(() => {
        if (!cancelled) setPatientInventory(null);
      });
    return () => { cancelled = true; };
  }, [isOpen, previewPatientId, previewTypeId]);

  useEffect(() => {
    if (!newProductData) return;
    // A saved appointment whose type, date and time are unchanged: the toggle
    // saves as it is flipped, so it shows the products as they are — on only
    // while every product is comped. Uncomping one product turns it off, so a
    // later Save does not comp everything again.
    if (appointmentId && !productFieldsChanged) {
      if (compRequestInFlightRef.current) return;
      const allComped = allProductsComped(newProductData);
      if (allComped !== null && Boolean(getValues('compAppointment')) !== allComped) {
        setValue('compAppointment', allComped);
      }
      return;
    }
    if (typeof newProductData.compAppointment === 'boolean') {
      const formComp = getValues('compAppointment');
      if (!formComp) {
        const hasProducts = Object.values(newProductData.products).some(
          (pt) => pt.pending.length > 0 || pt.disbursed.length > 0 || pt.purchaseOthers.length > 0,
        );
        if (hasProducts) {
          setValue('compAppointment', newProductData.compAppointment);
        }
      }
    }
  }, [newProductData, appointmentId, productFieldsChanged, setValue, getValues]);

  // Clear type when group changes and the current type isn't in the group
  useEffect(() => {
    if (watchedGroupId && watchedTypeId) {
      const type = types.find((t) => t.appointmentTypeId === watchedTypeId);
      if (type && type.appointmentTypeGroupId && type.appointmentTypeGroupId !== watchedGroupId) {
        setValue('appointmentTypeId', undefined);
      }
    }
  }, [watchedGroupId, watchedTypeId, types, setValue]);

  // Auto-populate facility when a category/group is selected that is tied to
  // a specific facility (appointmentTypeGroupFacilityId !== 0).
  //
  // Ref-guarded so this only fires on an actual *group* change. Keying it off
  // `watchedFacilityId` instead made the facility select feel locked: picking a
  // facility the current group isn't available for immediately snapped the
  // facility back to the group's own facility. Facility is the wider choice, so
  // changing it now clears the group instead (see the effect below).
  const autoFacilityForGroupRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (!watchedGroupId) {
      autoFacilityForGroupRef.current = undefined;
      return;
    }
    // Wait for the type catalog before deciding — otherwise we'd mark the group
    // as handled while there is nothing to look its facility up in.
    if (types.length === 0) return;
    if (autoFacilityForGroupRef.current === watchedGroupId) return;
    autoFacilityForGroupRef.current = watchedGroupId;

    const sample = types.find(
      (t) => t.appointmentTypeGroupId === watchedGroupId && t.appointmentTypeGroupFacilityId !== 0,
    );
    if (sample && sample.appointmentTypeGroupFacilityId !== getValues('facilityId')) {
      setValue('facilityId', sample.appointmentTypeGroupFacilityId);
    }
  }, [watchedGroupId, types, setValue, getValues]);

  // Inverse of the above: when the user switches to a facility the selected
  // group isn't offered at, clear the group and type so they can pick from the
  // new facility's groups (`groupOptions` already re-filters on facility).
  //
  // Only user-driven changes count — we require a previous non-null facility
  // recorded while the modal was open, so opening an existing appointment whose
  // stored facility disagrees with its group never silently wipes the group.
  const groupFacilityGuardRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (!isOpen) {
      groupFacilityGuardRef.current = undefined;
      return;
    }
    const prevFacilityId = groupFacilityGuardRef.current;
    groupFacilityGuardRef.current = watchedFacilityId;

    if (!prevFacilityId || !watchedFacilityId || prevFacilityId === watchedFacilityId) return;
    if (!watchedGroupId || types.length === 0) return;

    const groupTypes = types.filter((t) => t.appointmentTypeGroupId === watchedGroupId);
    if (groupTypes.length === 0) return;

    const availableHere = groupTypes.some(
      (t) =>
        t.appointmentTypeGroupFacilityId === 0 ||
        t.appointmentTypeGroupFacilityId === watchedFacilityId,
    );
    if (!availableHere) {
      setValue('appointmentTypeId', undefined);
      setValue('appointmentTypeGroupId', undefined);
    }
  }, [isOpen, watchedFacilityId, watchedGroupId, types, setValue]);

  // ─── First-Appointment auto-check + duration sync (P7) ───────────────────
  // Mirrors legacy `appointment-schedule.js::loadApptDefaultDetails`
  // (L820-L932) and `loadFirstApptDetails` (L2714-L2770). When the user
  // selects an appointment type (or attaches/detaches a patient), we fetch
  // patient-scoped defaults from the back-end and apply the legacy
  // auto-check rule: First Appointment is auto-checked when this is the
  // patient's first time with this type AND the type's required products
  // are empty or all match the legacy keyword set
  // ("optims" / "tech credit(s)") — OR when `firstProduct` is true and the
  // keyword check failed. The duration is then `appointmentTypeDuration +
  // firstAppointmentTypeDuration` (base only when the box is unchecked).
  // Reference: docs/audit/appt-details-parity.md §1.4 + §1.8 + §5.C.12.
  const watchedFirstAppointment = watch('firstAppointment');
  const autoCheckAppliedForTypeRef = useRef<number | null>(null);
  const lastFirstApptCheckedRef = useRef<boolean | undefined>(undefined);
  const editTimeCorrectedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      autoCheckAppliedForTypeRef.current = null;
      lastFirstApptCheckedRef.current = undefined;
      editTimeCorrectedRef.current = false;
      setFirstApptDefaults(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!watchedTypeId) {
      setFirstApptDefaults(null);
      return;
    }
    let cancelled = false;
    appointmentsService
      .getAppointmentTypePatientDefaults(watchedTypeId, watchedPatientId ?? null)
      .then((d) => {
        if (!cancelled) setFirstApptDefaults(d);
      })
      .catch(() => {
        if (!cancelled) setFirstApptDefaults(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, watchedTypeId, watchedPatientId]);

  // Auto-check + duration reset on type change. Skipped on initial mount in
  // edit mode for the appointment's persisted type so we honour the value
  // already stored in `appointment_addons`.
  useEffect(() => {
    if (!firstApptDefaults) return;
    const tid = firstApptDefaults.appointmentTypeId;
    if (autoCheckAppliedForTypeRef.current === tid) return;
    autoCheckAppliedForTypeRef.current = tid;
    if (appointment && appointment.appointmentTypeId === tid) {
      // Persisted state wins — sync our toggle-tracker so a subsequent
      // user change still recomputes duration.
      lastFirstApptCheckedRef.current = appointment.firstAppointment ?? false;
      return;
    }

    let checkfirstappt = false;
    if (firstApptDefaults.productTypes.length >= 1) {
      for (const pt of firstApptDefaults.productTypes) {
        const n = (pt.productTypeName ?? '').toLowerCase();
        if (n === 'optims' || n === 'tech credits' || n === 'tech credit') {
          checkfirstappt = true;
          break;
        }
      }
    } else {
      checkfirstappt = true;
    }

    const shouldCheck =
      (checkfirstappt && firstApptDefaults.firstAppointment) ||
      (!checkfirstappt && firstApptDefaults.firstProduct);

    setValue('firstAppointment', shouldCheck);
    lastFirstApptCheckedRef.current = shouldCheck;

    const base = firstApptDefaults.appointmentTypeDuration || 30;
    const extra = shouldCheck ? firstApptDefaults.firstAppointmentTypeDuration || 0 : 0;
    setValue('duration', base + extra);
  }, [firstApptDefaults, appointment, setValue]);

  // Auto-populate resources from `appointment_resources_defaults` when the
  // user selects an appointment type. Mirrors legacy
  // `loadApptDetailResourceDefaults` (appointment-schedule.js L7032-L7056).
  // Skipped on initial edit-load so persisted resources aren't overwritten.
  useEffect(() => {
    if (!firstApptDefaults) return;
    const tid = firstApptDefaults.appointmentTypeId;
    if (appointment && appointment.appointmentTypeId === tid) return;
    const ids = firstApptDefaults.defaultResourceIds;
    if (ids && ids.length > 0) {
      setSelectedResourceIds(new Set(ids));
    }
  }, [firstApptDefaults, appointment]);

  // User-driven toggle of First Appointment recomputes the duration the
  // same way legacy `loadFirstApptDetails` L2747 does.
  useEffect(() => {
    if (!firstApptDefaults) return;
    const last = lastFirstApptCheckedRef.current;
    lastFirstApptCheckedRef.current = watchedFirstAppointment;
    if (last === undefined) return;
    if (last === watchedFirstAppointment) return;

    const base = firstApptDefaults.appointmentTypeDuration || 30;
    const extra = watchedFirstAppointment
      ? firstApptDefaults.firstAppointmentTypeDuration || 0
      : 0;
    setValue('duration', base + extra);
  }, [watchedFirstAppointment, firstApptDefaults, setValue]);

  // ─── Conflict detection (drives the validation strip) ────────────────────
  // AR-2: legacy `appointment-schedule.js` L6674 runs conflictCheck() once per
  // resource. We replicate the fan-out with Promise.all, dedupe the merged
  // conflicts/warnings by `${appointmentId}:${conflictType}:${resourceId}`
  // (resourceId falls back to the owning fan-out call when the server doesn't
  // echo it), and expose a per-resource map for row-level indicators.
  const runConflictCheck = useCallback(async () => {
    const resourceIds = [...selectedResourceIds];
    if (!watchedDate || !watchedStartTime || resourceIds.length === 0) {
      setConflicts([]);
      setWarnings([]);
      setResourceConflicts({});
      return;
    }

    setCheckingConflicts(true);
    try {
      const startNaive = `${watchedDate} ${watchedStartTime}:00`;
      const settled = await Promise.all(
        resourceIds.map(async (resourceId) => {
          try {
            const slots = await appointmentsService.checkAvailability({
              typeId: watchedTypeId,
              resourceId,
              date: watchedDate,
              duration: watchedDuration,
              facilityId: watchedFacilityId,
              startTime: startNaive,
              appointmentId: appointmentId,
              // Legacy `checkForBlocks` (appointment-schedule.js L7942–7943)
              // forwards apptPatientID + apptPlanID so SlotAnalyzer can resolve
              // requiresPatientPlan against the actual attached context.
              // Reference: docs/audit/appt-details-parity.md §5.B.
              patientId: watchedPatientId,
              planId: watchedPatientPlanId,
            });
            const matching = slots.find((s) => s.startTime === startNaive);
            return {
              resourceId,
              conflicts: matching?.conflicts ?? [],
              warnings: matching?.warnings ?? [],
            };
          } catch {
            return { resourceId, conflicts: [], warnings: [] };
          }
        }),
      );

      const perResource: Record<number, ConflictInfo[]> = {};
      const conflictBag = new Map<string, ConflictInfo>();
      const warningBag = new Map<string, ConflictInfo>();
      const self = appointmentId ?? null;

      for (const result of settled) {
        const filteredConflicts = self
          ? result.conflicts.filter((c) => c.appointmentId !== self)
          : result.conflicts;
        const filteredWarnings = self
          ? result.warnings.filter((c) => c.appointmentId !== self)
          : result.warnings;

        perResource[result.resourceId] = filteredConflicts;

        for (const c of filteredConflicts) {
          const key = `${c.appointmentId}:${c.conflictType}:${c.resourceId ?? result.resourceId}`;
          if (!conflictBag.has(key)) conflictBag.set(key, c);
        }
        for (const w of filteredWarnings) {
          const key = `${w.appointmentId}:${w.conflictType}:${w.resourceId ?? result.resourceId}`;
          if (!warningBag.has(key)) warningBag.set(key, w);
        }
      }

      setConflicts([...conflictBag.values()]);
      setWarnings([...warningBag.values()]);
      setResourceConflicts(perResource);
    } catch {
      setConflicts([]);
      setWarnings([]);
      setResourceConflicts({});
    } finally {
      setCheckingConflicts(false);
    }
  }, [
    watchedDate,
    watchedStartTime,
    watchedDuration,
    watchedFacilityId,
    selectedResourceIds,
    watchedTypeId,
    watchedPatientId,
    watchedPatientPlanId,
    appointmentId,
  ]);

  useEffect(() => {
    void runConflictCheck();
  }, [runConflictCheck]);

  // ─── Facility-tz reprojection of typed status timestamps ─────────────────
  // Legacy `updateFacilityDateTime` (`scheduler-appt-details.php` L1156–L1188):
  // when the user picks a different facility, every typed status timestamp
  // (Contact Attempted / Confirmed / Check In / Check Back / Check Out /
  // Cancelled) is re-projected from the previous facility's IANA tz to the
  // new one so the underlying real-world instant is preserved. Start/end
  // times are also reprojected so the user sees the correct wall-clock in
  // the new facility's timezone.
  // Reference: docs/audit/appt-details-parity.md §5.C.5 + P4.
  const prevFacilityIdRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (!isOpen) {
      prevFacilityIdRef.current = undefined;
      return;
    }
    const prevId = prevFacilityIdRef.current;
    prevFacilityIdRef.current = watchedFacilityId;
    if (prevId === undefined) return;
    if (!prevId || !watchedFacilityId || prevId === watchedFacilityId) return;

    const prevTz = facilities.find((f) => f.facilityId === prevId)?.ianaTimezone
      ?? accountIanaTimezone;
    const nextTz = facilities.find((f) => f.facilityId === watchedFacilityId)?.ianaTimezone
      ?? accountIanaTimezone;
    if (prevTz === nextTz) return;

    // Reproject start/end times to the new facility timezone
    const curDate = getValues('date');
    const curStart = getValues('startTime');
    const curEnd = getValues('endTime');
    if (curDate && curStart) {
      const reprojected = reprojectDateTime(curDate, curStart, prevTz, nextTz);
      setValue('date', reprojected.date);
      setValue('startTime', reprojected.time);
    }
    if (curDate && curEnd) {
      const reprojected = reprojectDateTime(curDate, curEnd, prevTz, nextTz);
      setValue('endTime', reprojected.time);
    }

    // Status timestamps deliberately do NOT move here. They are held as Pacific
    // wall-clock — a zone-independent anchor for one real instant — and StatusRow
    // renders them through `pacificIsoToFacilityLocal`, so switching facility
    // already re-labels the same instant in the new zone. Reprojecting the state
    // too shifted it a second time and corrupted the value.
  }, [watchedFacilityId, facilities, isOpen, accountIanaTimezone, getValues, setValue]);

  // ─── Derived ──────────────────────────────────────────────────────────────
  // Legacy `hideApptTypeGroups` (calendar-test.php L5893): when a facility is
  // selected, only show groups whose `groupFacilityID` is `0` (global) or
  // matches the active facility. With no facility selected we fall back to
  // showing every group, matching legacy behavior on a brand-new appt that
  // has not yet picked a facility. Reference: docs/audit §1.4 + §5.C.4.
  const groupOptions: SelectOption<number>[] = useMemo(() => {
    const seen = new Map<number, string>();
    for (const t of types) {
      if (!t.appointmentTypeGroupId || seen.has(t.appointmentTypeGroupId)) continue;
      if (!t.appointmentTypeGroupActive) continue;
      if (
        watchedFacilityId &&
        t.appointmentTypeGroupFacilityId !== 0 &&
        t.appointmentTypeGroupFacilityId !== watchedFacilityId
      ) {
        continue;
      }
      const name = t.appointmentTypeGroupName ?? `Group #${t.appointmentTypeGroupId}`;
      if (/----/.test(name)) continue;
      seen.set(t.appointmentTypeGroupId, name);
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [types, watchedFacilityId]);

  const typeOptions: SelectOption<number>[] = useMemo(() => {
    if (!watchedGroupId) return [];
    return types
      .filter((t) => t.appointmentTypeGroupId === watchedGroupId)
      .map((t) => ({ value: t.appointmentTypeId, label: t.appointmentTypeName }));
  }, [types, watchedGroupId]);

  // AR-3: pull live aa_role titles so "Doctors/Nurses/Coordinators" group
  // headings reflect each account's configured terminology (legacy
  // `appointment-schedule.js` L6299-6324).
  const { titles: roleTitleOverrides } = useRoleTitles();
  const resourceTypeLabels = useMemo<Record<string, string>>(() => {
    const merged: Record<string, string> = { ...RESOURCE_TYPE_LABELS };
    for (const [singular, roleKey] of Object.entries(RESOURCE_TYPE_TO_ROLE_KEY)) {
      const override = roleTitleOverrides[roleKey];
      if (override && override.trim().length > 0) merged[singular] = override;
    }
    return merged;
  }, [roleTitleOverrides]);

  // AR-4: materialize the row list. Committed rows come from
  // `selectedResourceIds` (preserving insertion order — Set preserves it), and
  // are typed by looking up the resource catalog. Draft rows (type picked, no
  // resource yet) render after the committed rows. Fallback to 'other' when a
  // resource isn't loaded in the catalog yet.
  const resourceRowListRows: ResourceRow[] = useMemo(() => {
    const committed: ResourceRow[] = [...selectedResourceIds].map((id) => {
      const found = resources.find((r) => r.resourceId === id);
      return {
        key: `committed-${id}`,
        resourceType: (found?.resourceType ?? 'other') as Resource['resourceType'],
        resourceId: id,
      };
    });
    const drafts: ResourceRow[] = rowDrafts.map((d) => ({
      key: d.key,
      resourceType: d.resourceType,
      resourceId: null,
    }));
    return [...committed, ...drafts];
  }, [selectedResourceIds, resources, rowDrafts]);

  const handleAddResourceRow = useCallback(() => {
    setRowDrafts((prev) => [
      ...prev,
      { key: `draft-${Date.now()}-${prev.length}`, resourceType: 'doctor' },
    ]);
  }, []);

  const handleChangeRowType = useCallback(
    (rowKey: string, next: Resource['resourceType']) => {
      if (rowKey.startsWith('draft-')) {
        setRowDrafts((prev) =>
          prev.map((d) => (d.key === rowKey ? { ...d, resourceType: next } : d)),
        );
        return;
      }
      // Committed row: changing the type clears the resource selection so the
      // user can pick from the new pool. We remove the committed id from the
      // set and push a draft in its place (keeping roughly the same slot).
      const committedId = Number(rowKey.replace('committed-', ''));
      setSelectedResourceIds((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(committedId);
        return nextSet;
      });
      setRowDrafts((prev) => [
        ...prev,
        { key: `draft-${Date.now()}-${prev.length}`, resourceType: next },
      ]);
    },
    [],
  );

  const handleChangeRowResource = useCallback(
    (rowKey: string, nextResourceId: number | null) => {
      if (rowKey.startsWith('draft-')) {
        setRowDrafts((prev) => prev.filter((d) => d.key !== rowKey));
        if (nextResourceId != null) {
          setSelectedResourceIds((prev) => {
            const nextSet = new Set(prev);
            nextSet.add(nextResourceId);
            return nextSet;
          });
        }
        return;
      }
      const committedId = Number(rowKey.replace('committed-', ''));
      setSelectedResourceIds((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(committedId);
        if (nextResourceId != null) nextSet.add(nextResourceId);
        return nextSet;
      });
    },
    [],
  );

  const handleRemoveRow = useCallback((rowKey: string) => {
    if (rowKey.startsWith('draft-')) {
      setRowDrafts((prev) => prev.filter((d) => d.key !== rowKey));
      return;
    }
    const committedId = Number(rowKey.replace('committed-', ''));
    setSelectedResourceIds((prev) => {
      const nextSet = new Set(prev);
      nextSet.delete(committedId);
      return nextSet;
    });
  }, []);

  const facilityOptions: SelectOption<number>[] = useMemo(
    () => facilities.filter((f) => f.active).map((f) => ({ value: f.facilityId, label: f.facilityName })),
    [facilities],
  );

  const patientAgeWarning = useMemo(() => {
    const dob = selectedPatient?.dateOfBirth ?? appointment?.patientDateOfBirth ?? null;
    const age = getAge(dob);
    if (age === null) return null;
    if (age < 18) return `This patient is ${age} years old (under 18).`;
    if (age > 69) return `This patient is ${age} years old (over 69).`;
    return null;
  }, [selectedPatient?.dateOfBirth, appointment?.patientDateOfBirth]);

  const isBlock = Boolean(appointment?.isBlock);
  const derivedStatus: AppointmentStatus = useMemo(() => {
    if (cancelledFlag || cancelledAt) return 'cancelled';
    if (noShowFlag) return 'no_show';
    if (checkOutAt) return 'completed';
    if (checkBackAt) return 'checked_back';
    if (checkInAt) return 'checked_in';
    if (confirmedFlag || confirmedAt) return 'confirmed';
    if (contactAttemptedAt) return 'contact_attempted';
    return 'scheduled';
  }, [cancelledFlag, cancelledAt, noShowFlag, checkOutAt, checkBackAt, checkInAt, confirmedFlag, confirmedAt, contactAttemptedAt]);

  const actionMessages = useMemo(() => {
    if (!appointment?.startTime) return [];
    const apptDate = new Date(appointment.startTime);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    if (apptDate >= todayStart) return [];

    const msgs: string[] = [];
    const isTerminal = derivedStatus === 'cancelled' || derivedStatus === 'no_show' || derivedStatus === 'completed';
    if (!isTerminal) {
      msgs.push('This appointment is past its scheduled date and has not been checked out.');
    }
    if (newProductData) {
      const hasPending = Object.values(newProductData.products).some((pt) => pt.pending.length > 0);
      if (hasPending) {
        msgs.push('This appointment has products that have not been disbursed.');
      }
    }
    return msgs;
  }, [appointment?.startTime, derivedStatus, newProductData]);

  // Legacy `eligibleForConfirmation` (scheduler-appt-details.php L380–636)
  // hides "Send Confirmation Now" when any of the following hold:
  //   (1) appt is in the past
  //   (2) confirmedDate is already set
  //   (3) patient.emailOptIn == 0 OR smsOptIn == 0
  //   (4) appt is brand-new (NEW)
  // Reference: docs/audit/appt-details-parity.md §1.5 row 1375 + §5.C.3.
  const canSendConfirmation = useMemo(() => {
    if (!appointmentId) return false;
    if (!selectedPatient?.patientId) return false;
    if (cancelledFlag) return false;
    if (confirmedAt) return false;

    const startLocal = watchedDate && watchedStartTime
      ? new Date(`${watchedDate}T${watchedStartTime}:00`)
      : null;
    if (startLocal && startLocal.getTime() < Date.now()) return false;

    if (appointment?.patientEmailOptIn === false) return false;
    if (appointment?.patientSmsOptIn === false) return false;

    return true;
  }, [
    appointmentId,
    selectedPatient?.patientId,
    cancelledFlag,
    confirmedAt,
    watchedDate,
    watchedStartTime,
    appointment?.patientEmailOptIn,
    appointment?.patientSmsOptIn,
  ]);

  // Prefer the live appointment-type notes from the patient-defaults
  // payload so the ribbon updates the moment the user picks a different
  // type — legacy `appointment-schedule.js` L891-L906 toggles
  // `[data-appttypenotes]` on every type change. Falls back to the
  // persisted notes for the initial mount before the API resolves.
  // Reference: docs/audit/appt-details-parity.md §1.4 + §5.C.12.
  const appointmentTypeNotes =
    (firstApptDefaults?.appointmentTypeNotes ?? '').trim() !== ''
      ? firstApptDefaults!.appointmentTypeNotes
      : appointment?.appointmentTypeNotes ?? null;
  const patientNotes = appointment?.patientNotes ?? null;

  const notesAutoExpandedRef = useRef(false);
  useEffect(() => {
    if (notesAutoExpandedRef.current) return;
    // Linked notes count as content too, so an appointment whose only note material is
    // a linked record still opens the section. Nothing in any of the four => stays
    // collapsed, as before.
    if (
      appointmentTypeNotes ||
      patientNotes ||
      (watch('reason') ?? '').trim() ||
      linkedNotes.length > 0
    ) {
      notesAutoExpandedRef.current = true;
      setCollapsed((prev) => ({ ...prev, notes: false }));
    }
  }, [appointmentTypeNotes, patientNotes, watch, linkedNotes.length]);

  // ─── Actions ──────────────────────────────────────────────────────────────

  const buildPayload = useCallback(
    // CreateAppointmentPayload, not `AppointmentWritableFields & Record<string,
    // unknown>`: this builds startTime and endTime below, and the old type
    // declared neither, so createAppointment was handed a value it could not
    // see them on. UpdateAppointmentPayload is the same shape with those two
    // optional, so it takes this as-is.
    (values: FormValues): CreateAppointmentPayload => {
      const endTime = computeEndTime(values.startTime, values.duration);
      const packedShortcodes = Object.fromEntries(
        Object.entries(shortcodeValues).filter(([, v]) => v?.trim() !== ''),
      );

      // `date`/`startTime` are already facility-local (the form-populate effect
      // renders them in the facility zone), but the status timestamps below are
      // Pacific in state, so they get projected into the same zone the server
      // will parse them in — the facility being saved, not the one currently
      // watched.
      const payloadTz =
        (values.facilityId
          ? facilities.find((f) => f.facilityId === values.facilityId)?.ianaTimezone
          : null) ?? accountIanaTimezone;
      const toFacility = (iso: string | null) => pacificIsoToFacilityIso(iso, payloadTz);

      return {
        name: values.name,
        startTime: `${values.date} ${values.startTime}:00`,
        endTime: `${values.date} ${endTime}:00`,
        duration: values.duration,
        patientId: values.patientId,
        patientPlanId: values.patientPlanId,
        appointmentTypeId: values.appointmentTypeId,
        facilityId: values.facilityId,
        reason: values.reason ?? '',
        waitList: values.waitList ?? false,
        resourceIds: [...selectedResourceIds],
        firstAppointment: values.firstAppointment ?? false,
        compAppointment: values.compAppointment ?? false,
        shortcodes: packedShortcodes,
        contactAttemptedAt: toFacility(contactAttemptedAt),
        confirmed: confirmedFlag,
        confirmedAt: toFacility(confirmedAt),
        confirmedContactMethod: confirmedContactMethod || null,
        checkInAt: toFacility(checkInAt),
        checkBackAt: toFacility(checkBackAt),
        checkOutAt: toFacility(checkOutAt),
        cancelled: cancelledFlag,
        cancelledAt: toFacility(cancelledAt),
        // Keep the text and the ID consistent: zeroing the ID while leaving the
        // stale title behind is what made an un-cancelled appointment come back
        // as "Other" + the old reason's text.
        cancelledReason: cancelledFlag ? cancelledReason : '',
        cancelReasonID: cancelledFlag ? (cancelReasonID ?? 0) : 0,
        noShow: noShowFlag,
        // Persist the required-set choice only when the type offers both sides
        // and a switch is actually possible. The server otherwise applies the
        // membership default.
        ...(newProductData?.hasServiceCredit &&
        newProductData?.hasRequiredProducts &&
        (productModeOverride ?? newProductData?.productMode)
          ? { product_mode: (productModeOverride ?? newProductData?.productMode) as ProductMode }
          : {}),
      };
    },
    [
      shortcodeValues,
      selectedResourceIds,
      newProductData,
      productModeOverride,
      facilities,
      accountIanaTimezone,
      contactAttemptedAt,
      confirmedFlag,
      confirmedAt,
      confirmedContactMethod,
      checkInAt,
      checkBackAt,
      checkOutAt,
      cancelledFlag,
      cancelledAt,
      cancelledReason,
      cancelReasonID,
      noShowFlag,
    ],
  );

  // Apply a manual required-set switch. For a new (unsaved) appointment we just
  // set the override — the products preview refetches with it. For a saved
  // appointment we persist immediately so the attached product records — the
  // source of truth — reflect the choice. We route through `handleSubmit` so
  // the payload uses the same zod-validated/coerced values a normal Save
  // produces (building it from raw `getValues()` sent malformed fields like an
  // empty `facilityId`, which the API rejected with a 422).
  const applyProductMode = useCallback(
    (mode: ProductMode) => {
      setProductModeOverride(mode);
      if (
        !isEditMode ||
        !appointmentId ||
        !selectedPatient?.patientId ||
        productFieldsChanged
      ) {
        return;
      }
      void handleSubmit(async (values) => {
        showAutoSaveOverlay();
        try {
          const payload = { ...buildPayload(values as unknown as FormValues), product_mode: mode };
          const res = await appointmentsService.updateAppointment(appointmentId, payload);
          reset(getValues(), { keepDirty: false });
          loadNewProducts();
          // Notify the parent so it refreshes the patient's appointment /
          // product lists (otherwise the swapped products show stale until a
          // manual page refresh).
          onSaved?.(res?.data ?? null);
        } catch (err) {
          toast.error(getErrorMessage(err, 'Could not switch required products'));
        } finally {
          hideAutoSaveOverlay();
        }
      })();
    },
    [
      isEditMode,
      appointmentId,
      selectedPatient?.patientId,
      productFieldsChanged,
      handleSubmit,
      buildPayload,
      getValues,
      reset,
      loadNewProducts,
      onSaved,
      showAutoSaveOverlay,
      hideAutoSaveOverlay,
    ],
  );

  // react-hook-form invokes this when client-side zod validation fails before
  // we ever reach `onSubmit`. We mirror the same banner so the user sees a
  // unified, friendly summary regardless of whether the failure was caught on
  // the client or returned by the API.
  const onInvalid = (formErrors: typeof errors) => {
    const fields = Object.entries(formErrors)
      .filter(([, err]) => !!err && typeof err === 'object' && 'message' in err && err.message)
      .map(([key, err]) => ({
        label: FIELD_LABEL[key] ?? key,
        message: String((err as { message?: string }).message ?? 'Required'),
      }));

    if (fields.length > 0) {
      setValidationBanner({
        title: 'Please fix the highlighted fields before saving:',
        fields,
      });
      toast.error('Please fix the highlighted fields');
    }
  };

  const onSubmit = async (values: FormValues) => {    
    if (cancelledFlag) {
      if (cancelReasonID === null) {
        toast.error('Please select a cancellation reason');
        return;
      }
      if (cancelReasonID === 0 && !cancelledReason.trim()) {
        toast.error('Please enter the cancellation reason details for "Other"');
        return;
      }
    }

    setSubmitting(true);
    setSaveOverlay('saving');
    setValidationBanner(null);
    clearErrors();
    try {
      const payload = buildPayload(values);    

      // scheduler_check_blocks parity: surface conflicts/warnings before save.
      // Legacy `saveAppt(apptID)` ran `checkConflicts(apptID)` for both edit
      // and "NEW" — see `services/calendar.php` L5371 — and blocked save
      // when `errorsCount > 0`. Match by running drag-check whenever we
      // have a candidate time + at least one resource, regardless of
      // edit mode. Backend treats `appointmentId == 0` as legacy "NEW".
      if (selectedResourceIds.size > 0 && values.date && values.startTime) {
        try {
          const startNaive = `${values.date} ${values.startTime}:00`;
          const durMinutes = values.duration ?? 30;
          const endNaive = `${values.date} ${computeEndTime(values.startTime, durMinutes)}:00`;
          // selectedResourceIds is a Set; JSON-serializing a Set yields
          // `{}` (empty object), which the API then treats as "no
          // resources applied". Materialize as a number[] so the
          // server sees the user's actual selection — and so the
          // maxConflicts logic in `SchedulerAvailabilityService` has
          // resources to iterate. For new appointments the controller
          // has no persisted-row fallback for patientId/planId either,
          // so forward them explicitly the same way the live availability
          // strip does (`runConflictCheck`, ~L2620-L2634).
          const check = await appointmentsService.dragCheck({
            appointmentId: appointmentId ?? 0,
            newStart: startNaive,
            newEnd: endNaive,
            resourceIds: [...selectedResourceIds],
            facilityId: values.facilityId ?? undefined,
            appointmentTypeId: values.appointmentTypeId ?? undefined,
            patientId: values.patientId && values.patientId > 0 ? values.patientId : undefined,
            planId: values.patientPlanId && values.patientPlanId > 0 ? values.patientPlanId : undefined,
          });
          setBlockWarnings(check.warnings ?? []);
          if ((check.conflicts?.length ?? 0) > 0) {
            setBlockConflicts(check.conflicts);
            if (!check.canSaveAnyway) {
              // Clear BOTH overlay + submitting so the user can see and
              // act on the surfaced conflict banner. Previously only
              // `submitting` was reset, leaving the "Saving..." spinner
              // permanently covering the modal.
              setSaveOverlay(null);
              setSubmitting(false);
              return;
            }
            // Build a category-aware confirm message so the user is not
            // told everything is a "block conflict" when the actual issue
            // is an existing-appointment overlap on a shared resource.
            const cat = categorizeConflicts(check.conflicts);
            const heading = buildConflictHeading(cat);
            const lines = check.conflicts.map((c) => {
              const t = c.conflictType ?? '';
              if (RESOURCE_CONFLICT_TYPES.has(t)) {
                const who = c.resourceName ? `${c.resourceName}: ` : '';
                return `• ${who}${formatResourceConflictLine(c, displayTz)}`;
              }
              return `• ${c.reason}`;
            });
            const proceed = window.confirm(`${heading}\n\n${lines.join('\n')}\n\nSave anyway?`);
            if (!proceed) {
              setSaveOverlay(null);
              setSubmitting(false);
              return;
            }
          } else {
            setBlockConflicts([]);
          }
        } catch {
          // Non-fatal: drag-check failure should not block save.
        }
      }

      const result = isEditMode && appointmentId
        ? await appointmentsService.updateAppointment(appointmentId, payload)
        : await appointmentsService.createAppointment(payload);

      const saved = result.data;
      const workflowMeta = result.meta?.workflow as Record<string, unknown> | undefined;

      toast.success(isEditMode ? 'Appointment saved' : 'Appointment created');

      // scheduler_check_stage.php parity — use the plan-stage recommendation
      // already computed server-side in runPostSaveParityHooks and returned
      // via meta.workflow.stageRecommendation (eliminates a second API call).
      const recommendation = workflowMeta?.stageRecommendation as {
        targetStageId: number;
        targetStageName: string | null;
        triggerAction: string;
      } | null | undefined;
      // saved.appointmentId, not `saved?.id` (not a field on Appointment):
      // reading `id` left this always false and the recommendation dropped.
      const willPromptStage = Boolean(saved?.appointmentId && recommendation);

      // With a stage prompt to show, onSaved waits for the answer (see
      // stagePendingSaved): the hosts close this modal in it.
      if (!willPromptStage) onSaved?.(saved);

      // Legacy "prompt to confirm" (appointment_confirm_type.promptConfirm /
      // admin "On appt. creation"): when an appointment is FIRST scheduled
      // (not edited) and is unconfirmed, ask whether to send the confirmation
      // now. Eligibility (confirm type flag, unconfirmed, future, opt-in) is
      // decided server-side and surfaced via meta.workflow.promptConfirm. On
      // accept we reuse the same "Send confirmation now" action as the Send
      // button on appointment details (handleSendConfirmation).
      if (!isEditMode && saved?.appointmentId && workflowMeta?.promptConfirm === true) {
        const proceed = window.confirm('Send an appointment confirmation to the patient now?');
        if (proceed) {
          try {
            const sendResult = await appointmentsService.sendConfirmation(saved.appointmentId);
            if (sendResult.smsSent || sendResult.emailSent) {
              toast.success('Confirmation sent');
            } else {
              toast.error('Confirmation not sent. The patient is not opted in, or no confirmation template is configured.');
            }
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to send confirmation');
          }
        }
      }

      // Reschedule notice: when an EDIT moved the appointment's start time and
      // the account has "text the patient when staff book" switched on, ask
      // whether to tell the patient. A reschedule is not automatically worth a
      // text — the patient may be standing at the desk agreeing to the new time
      // — so the person who moved it decides. Eligibility (start time actually
      // changed, account setting, not cancelled, still in the future) is decided
      // server-side and surfaced via meta.workflow.promptBookedSmsUpdate.
      //
      // saved.appointmentId, NOT the id we opened with: an edit is audit-
      // versioned, so the save inserted a NEW row and the old id now points at a
      // superseded version the sender would refuse.
      if (isEditMode && saved?.appointmentId && workflowMeta?.promptBookedSmsUpdate === true) {
        await promptAndSendBookedSmsUpdate(saved.appointmentId, toast);
      }

      if (willPromptStage && saved?.appointmentId && recommendation) {
        setStageRecommendation({
          targetStageId: recommendation.targetStageId,
          targetStageName: recommendation.targetStageName,
          triggerAction: recommendation.triggerAction,
        });
        setStageSavedId(saved.appointmentId);
        setStagePendingSaved(saved);
        setSaveOverlay(null);
        return; // leave modal open so the user can confirm stage change
      }

      setSaveOverlay(null);
      onClose();
    } catch (err) {
      setSaveOverlay(null);
      const fieldErrors = extractFieldErrors(err);
      const baseMessage = getErrorMessage(err, 'Failed to save appointment');

      if (Object.keys(fieldErrors).length > 0) {
        const friendly: Array<{ label: string; message: string }> = [];

        // De-dupe form fields so e.g. `name` and `scheduledAppointmentName`
        // both surface as a single Title error instead of two.
        const seenFormFields = new Set<keyof FormValues>();

        for (const [serverKey, rawMsg] of Object.entries(fieldErrors)) {
          // Strip `resourceIds.0`-style array indices before lookup.
          const baseKey = serverKey.split('.')[0];
          const friendlyMsg = humanizeServerMessage(baseKey, rawMsg);
          const formField = SERVER_FIELD_TO_FORM[baseKey];

          if (formField) {
            if (seenFormFields.has(formField)) continue;
            seenFormFields.add(formField);
            setError(formField, { type: 'server', message: friendlyMsg });
            friendly.push({
              label: FIELD_LABEL[formField] ?? formField,
              message: friendlyMsg,
            });
          } else {
            const label = FIELD_LABEL[baseKey] ?? baseKey;
            friendly.push({ label, message: friendlyMsg });
          }
        }

        setValidationBanner({
          title: 'Please fix the highlighted fields before saving:',
          fields: friendly,
        });
        toast.error('Please fix the highlighted fields');
      } else {
        toast.error(baseMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Answering the stage prompt, either way, ends the save: the held-back
  // onSaved fires now, after any stage move, so the host's refresh shows it.
  const finishStagePrompt = () => {
    const pending = stagePendingSaved;
    setStageRecommendation(null);
    setStageSavedId(null);
    setStagePendingSaved(null);
    if (pending) onSaved?.(pending);
    onClose();
  };

  const handleConfirmStageChange = async () => {
    // stage-apply runs the whole stage processor (history row, tasks,
    // automations that message the patient), so a second click must not
    // send a second request.
    if (!stageRecommendation || !stageSavedId || stageApplying) return;
    setStageApplying(true);
    try {
      await appointmentsService.stageApply(stageSavedId, stageRecommendation.targetStageId);
      toast.success('Plan stage updated');
    } catch (err) {
      console.error('stage-apply failed', err);
      // The appointment itself is already saved. These four are refused
      // before anything is written (AppointmentExtrasController::stageApply;
      // INVALID_STAGE is applyStage's "Plan not found", thrown before the
      // trigger runs). Anything else may have failed part way through.
      const code = getErrorCode(err);
      if (code === 'NOT_FOUND' || code === 'VALIDATION_ERROR' || code === 'NO_PLAN' || code === 'INVALID_STAGE') {
        toast.error(
          'Plan stage not changed',
          "The appointment was saved. Change the stage on the patient's plan instead.",
        );
      } else {
        toast.error(
          'Plan stage may not have changed',
          "The appointment was saved. Check the stage on the patient's plan and change it there if it did not move.",
        );
      }
    } finally {
      setStageApplying(false);
      finishStagePrompt();
    }
  };

  const handleDeclineStageChange = () => {
    if (stageApplying) return;
    finishStagePrompt();
  };

  const handleCopy = () => {
    if (!appointment) return;
    const values = getValues();
    copySnapshotRef.current = {
      title: values.name ?? '',
      startTime: values.date && values.startTime ? `${values.date}T${values.startTime}:00` : null,
      endTime: values.date && values.endTime ? `${values.date}T${values.endTime}:00` : null,
      duration: values.duration && values.duration > 0 ? values.duration : 0,
      facilityId: values.facilityId && values.facilityId > 0 ? values.facilityId : 0,
      appointmentTypeId:
        values.appointmentTypeId && values.appointmentTypeId > 0
          ? values.appointmentTypeId
          : 0,
      appointmentTypeGroupId:
        values.appointmentTypeGroupId && values.appointmentTypeGroupId > 0
          ? values.appointmentTypeGroupId
          : 0,
      resourceIds: [...selectedResourceIds],
      notes: values.reason ?? '',
      patientId: values.patientId && values.patientId > 0 ? values.patientId : 0,
      planId: values.patientPlanId && values.patientPlanId > 0 ? values.patientPlanId : 0,
      patient: selectedPatient ?? null,
    };
    setCopyMode(true);
  };

  // P8 — "Find Available Time" toolbar action. Snapshots the type / group /
  // facility / duration / resources, identical to legacy `loadNextAvailPage`
  // arguments at `scheduler-appt-details.php` L760, and hands them to the
  // parent so it can navigate to the Next-Available view. Hidden when the
  // parent does not provide a handler so we don't render a dead button.
  const handleFindAvailableTime = () => {
    if (!onFindAvailableTime) return;
    const values = getValues();
    onFindAvailableTime({
      appointmentTypeId:
        values.appointmentTypeId && values.appointmentTypeId > 0
          ? values.appointmentTypeId
          : undefined,
      appointmentTypeGroupId:
        values.appointmentTypeGroupId && values.appointmentTypeGroupId > 0
          ? values.appointmentTypeGroupId
          : undefined,
      facilityId: values.facilityId && values.facilityId > 0 ? values.facilityId : undefined,
      duration: values.duration && values.duration > 0 ? values.duration : undefined,
      resourceIds: [...selectedResourceIds],
    });
  };

  const handleHardDelete = () => {
    if (!appointmentId) return;
    setShowDeleteConfirm(true);
  };

  const confirmHardDelete = async () => {
    if (!appointmentId) return;
    setShowDeleteConfirm(false);
    setDeleting(true);
    try {
      await appointmentsService.hardDeleteAppointment(appointmentId);
      toast.success('Appointment deleted');
      onSaved?.(null);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendConfirmation = async () => {
    if (!appointmentId) return;
    setSendingConfirmation(true);
    try {
      // Channel (SMS / email / both) is decided server-side by the appointment's
      // confirm type, matching legacy. This only sends + logs; it does not
      // change the confirmed status (the Send button stays until a confirmed
      // date is set via Save or the patient acts on the link).
      const result = await appointmentsService.sendConfirmation(appointmentId);
      if (result.smsSent || result.emailSent) {
        toast.success('Confirmation sent');
      } else {
        toast.error('Confirmation not sent. The patient is not opted in, or no confirmation template is configured.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send confirmation');
    } finally {
      setSendingConfirmation(false);
    }
  };

  const handleRemovePlan = async () => {
    if (!appointmentId || !selectedPatient?.patientId) return;
    try {
      const updated = await appointmentsService.setAppointmentPatient(
        appointmentId,
        selectedPatient.patientId,
        null,
      );
      setFormPlan({ planId: undefined, chosenInPicker: false });
      onSaved?.(updated);
      toast.success('Plan unlinked');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unlink plan');
    }
  };

  const handleAttachPlanFromPicker = async (planId: number) => {
    if (!appointmentId || !selectedPatient?.patientId) return;
    try {
      const updated = await appointmentsService.setAppointmentPatient(
        appointmentId,
        selectedPatient.patientId,
        planId,
      );
      setFormPlan({ planId, chosenInPicker: false });
      onSaved?.(updated);
      setPlanPickerOpen(false);
      toast.success('Plan attached');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to attach plan'));
    }
  };

  // New Plan handler: legacy `addPlanButton` (`scheduler-appt-details.php`
  // L768) calls `loadNewLeadForm(patientID)`. v5 reuses `PlanCreateModal`
  // seeded with the attached patient; on creation we eagerly link the new
  // plan to the appointment via `setAppointmentPatient` so the modal updates
  // its plan pill on the next render.
  const handleNewPlanCreated = async (createdPlanId: number) => {
    setNewPlanOpen(false);
    if (!appointmentId || !selectedPatient?.patientId || !createdPlanId) return;
    try {
      const updated = await appointmentsService.setAppointmentPatient(
        appointmentId,
        selectedPatient.patientId,
        createdPlanId,
      );
      setFormPlan({ planId: createdPlanId, chosenInPicker: false });
      onSaved?.(updated);
      toast.success('New plan attached');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Plan created but failed to attach'));
    }
  };

  // "Add Patient" beside the search: the intake form has committed a patient and
  // a plan (`POST /leads/intake` always creates both). Land them in the form the
  // same way picking from the search does — staged, not persisted, so a single
  // Save writes the appointment and its patient/plan link together.
  const handlePatientCreated = async (created: CreatedIntakePatient) => {
    setAddPatientOpen(false);

    // The intake response carries no displayID, so the header would render the
    // name without "#1234" unlike a search-picked patient. One cheap read fixes
    // that; the locally-known name is the fallback if it fails.
    let summary = buildSelectedPatient({
      patientId: created.patientId,
      firstName: created.firstName,
      lastName: created.lastName,
      dateOfBirth: created.birthDate,
    });
    try {
      const fetched = await getIntakePatient(created.patientId);
      summary = buildSelectedPatient({
        patientId: fetched.patientId,
        firstName: fetched.firstName,
        lastName: fetched.lastName,
        displayId: fetched.displayId,
        dateOfBirth: fetched.birthDate,
      }) ?? summary;
    } catch {
      /* keep the locally-built summary */
    }
    if (!summary) return; // buildSelectedPatient returns null on an empty name

    setSelectedPatient(summary);
    setValue('patientId', created.patientId, { shouldDirty: true });
    setFormPlan({ planId: created.planId, chosenInPicker: false }, { shouldDirty: true });
    setChangingPatient(false);
    // Deliberate wording: the patient and plan rows already exist server-side
    // while this appointment does not, so don't imply the link is saved.
    toast.success('Patient created — save the appointment to attach it');
  };

  // ─── Close with unsaved-changes guard ────────────────────────────────────
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const confirmClose = useCallback(() => {
    setShowUnsavedConfirm(false);
    onClose();
  }, [onClose]);

  // These three feed both the modal header (far below) and handleAddNote's chip
  // label. They must be declared BEFORE handleAddNote: a useCallback dependency
  // array is evaluated during render, so referencing them from below would throw
  // "Cannot access 'headerDateTimeStr' before initialization". Moving handleAddNote
  // down instead is not an option — `footer` references it.
  const headerTypeName = useMemo(() => {
    if (watchedTypeId) {
      const t = types.find((t) => t.appointmentTypeId === watchedTypeId);
      if (t?.appointmentTypeName) return t.appointmentTypeName;
    }
    return appointment?.appointmentTypeName ?? null;
  }, [watchedTypeId, types, appointment]);

  const headerFacilityName = useMemo(() => {
    if (watchedFacilityId) {
      const f = facilities.find((f) => f.facilityId === watchedFacilityId);
      if (f?.facilityName) return f.facilityName;
    }
    return null;
  }, [watchedFacilityId, facilities]);

  const headerDateTimeStr = useMemo(() => {
    if (!watchedDate) return null;
    const d = new Date(`${watchedDate}T12:00:00`);
    const datePart = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    if (!watchedStartTime) return datePart;
    const [hh, mm] = watchedStartTime.split(':').map(Number);
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    const timePart = `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
    return `${datePart} at ${timePart}`;
  }, [watchedDate, watchedStartTime]);

  const linkedNotesLabel = useMemo(
    () => [headerTypeName, headerDateTimeStr].filter(Boolean).join(' · '),
    [headerTypeName, headerDateTimeStr],
  );

  const noteForm = useNoteForm();

  const handleAddNote = useCallback(() => {
    if (!selectedPatient?.patientId) return;
    noteForm.openNote({
      patientId: String(selectedPatient.patientId),
      noteId: null,
      patientName: selectedPatient.displayName ?? undefined,
      // Only pass a link target once the appointment row actually exists — there is
      // nothing to link to in create/copy mode.
      linkAppointmentId: isEditMode ? appointmentId : undefined,
      linkAppointmentLabel: isEditMode ? linkedNotesLabel || undefined : undefined,
    });
  }, [noteForm, selectedPatient, isEditMode, appointmentId, linkedNotesLabel]);

  // ─── Footer ───────────────────────────────────────────────────────────────

  /*
    SCRUM-1406/1408 both report the footer "overlapping the form". It does not — the
    shared Modal is `flex flex-col` with a `flex-shrink-0` footer, so it is a flow
    sibling and cannot overlap. In edit mode this row carries up to four secondary
    buttons plus "Unsaved changes" plus Create, wraps to three or four lines on a
    phone, and takes that height out of the body's `flex-1` share — which is what
    hides the section below it. Stacking the two groups and letting the buttons fill
    the width keeps the footer to two predictable rows.
  */
  const footer = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2 max-md:flex-col max-md:items-stretch">
      <div className="flex flex-wrap items-center gap-1.5 max-md:grid max-md:w-full max-md:grid-cols-2">
        {onFindAvailableTime ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<CalendarSearch className="h-4 w-4" />}
            onClick={handleFindAvailableTime}
            disabled={submitting || deleting}
          >
            Find Available Time
          </Button>
        ) : null}
        {isEditMode && canDeleteAppointment ? (
          <Button
            type="button"
            variant="danger"
            size="sm"
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={handleHardDelete}
            loading={deleting}
            disabled={submitting || deleting}
          >
            Delete
          </Button>
        ) : null}
        {isEditMode ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<CopyIcon className="h-4 w-4" />}
            onClick={handleCopy}
            disabled={submitting || deleting}
          >
            Copy
          </Button>
        ) : null}
        {isEditMode && selectedPatient?.patientId ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<FileText className="h-4 w-4" />}
            onClick={handleAddNote}
            disabled={submitting || deleting}
          >
            Add Note
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-3 max-md:w-full max-md:justify-between">
        {hasUnsavedChanges && (
          <span className="text-xs font-medium text-amber-600">Unsaved changes</span>
        )}
        {/* Sized for the row rather than left floating against empty space —
            SCRUM-1406's "Create is right-aligned with a lot of empty space". */}
        <Button
          type="submit"
          form="unified-appt-form"
          variant="primary"
          size="sm"
          className="max-md:flex-1"
          loading={submitting || newProductsLoading}
          disabled={submitting || deleting || newProductsLoading}
        >
          {newProductsLoading ? 'Loading Products…' : isEditMode ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  );

  const isTerminalStatus = derivedStatus === 'cancelled' || derivedStatus === 'no_show' || derivedStatus === 'completed';
  const isCheckedIn = !!checkInAt && !checkOutAt && !isTerminalStatus;

  const headerActionLabel = useMemo(() => {
    if (isTerminalStatus) return null;
    if (isCheckedIn) return 'Check Out';
    return 'Check In';
  }, [isTerminalStatus, isCheckedIn]);

  const warnIfProductsShort = useCallback(() => {
    const data = productDataRef.current;
    if (!data?.products) return;
    const isComped = Boolean(getValues('compAppointment')) || data.compAppointment;
    if (isComped) return;
    const pts = Object.values(data.products);
    const hasPendingSlots = pts.some(
      (pt) => (pt.pending?.length ?? 0) > 0 || (pt.purchaseOthers?.length ?? 0) > 0,
    );
    if (!hasPendingSlots) return;
    const estimatedCost = pts.reduce(
      (s, pt) => s + parseCurrency(pt.curRate) * ((pt.pending?.length ?? 0) + (pt.purchaseOthers?.length ?? 0)),
      0,
    );
    setProductAlertCost(estimatedCost);
  }, [getValues]);

  const handleHeaderAction = useCallback(() => {
    warnIfProductsShort();
    if (isCheckedIn) {
      setCheckOutAt(getNowPacific());
    } else {
      setCheckInAt(getNowPacific());
    }
    setStatusMenuOpen(false);
  }, [isCheckedIn, warnIfProductsShort]);

  const handleCheckInChange = useCallback((v: string | null) => {
    if (v && !checkInAt) warnIfProductsShort();
    setCheckInAt(v);
  }, [checkInAt, warnIfProductsShort]);

  const handleCheckOutChange = useCallback((v: string | null) => {
    if (v && !checkOutAt) warnIfProductsShort();
    setCheckOutAt(v);
  }, [checkOutAt, warnIfProductsShort]);

  /**
   * Clearing the Cancelled timestamp un-cancels the appointment, so the reason
   * has to go with it — otherwise re-cancelling re-opens the picker holding the
   * reverted cancellation's reason.
   */
  const handleCancelledAtChange = useCallback((v: string | null) => {
    setCancelledAt(v);
    setCancelledFlag(Boolean(v));
    if (!v) {
      setCancelReasonID(null);
      setCancelledReason('');
    }
  }, []);

  const handleHeaderCancel = useCallback(() => {
    setCancelledAt(getNowPacific());
    setCancelledFlag(true);
    setStatusMenuOpen(false);
  }, []);

  const handleHeaderNoShow = useCallback(() => {
    setNoShowFlag(true);
    setStatusMenuOpen(false);
  }, []);

  const modalTitle = isEditMode ? (
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0 space-y-1.5">
        {selectedPatient && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-base font-semibold text-slate-900 hover:text-blue-700 hover:underline truncate"
              onClick={() => {
                visitPatient({
                  patientId: selectedPatient.patientId!,
                  name: selectedPatient.displayName ?? `#${selectedPatient.patientId}`,
                });
                window.open(`/patients/${selectedPatient.patientId}`, '_blank');
              }}
            >
              {privacyMode ? '\u2014' : selectedPatient.displayName}
              {selectedPatient.displayId && !privacyMode ? (
                <span className="ml-1 font-mono text-xs font-normal opacity-60">#{selectedPatient.displayId}</span>
              ) : null}
            </button>
            {/* Hidden under privacy mode along with the name it annotates. */}
            {!privacyMode && <PatientMemberBadge patientId={selectedPatient.patientId} />}
            <ChangePatientButton active={changingPatient} onClick={() => setChangingPatient((v) => !v)} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-base">
          {headerTypeName && (
            <span className="inline-flex items-center gap-1 font-semibold text-blue-900">
              <Tag className="h-4 w-4" />
              {headerTypeName}
            </span>
          )}
          {watch('waitList') ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-amber-600">Wait List</span></> : null}
          {watch('compAppointment') ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-amber-600">COMP</span></> : null}
          {watch('firstAppointment') ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-blue-600">First Time</span></> : null}
          {/* Derived server-side and READ-ONLY — deliberately read from the
              appointment, not from the form: there is no switch for it and it
              is never part of the write payload. */}
          {appointment?.firstVisit ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-violet-600">First Visit</span></> : null}
        </div>
        {(headerDateTimeStr || headerFacilityName) && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
            {headerDateTimeStr && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {headerDateTimeStr}
                {tzAbbreviation && <span className="text-slate-400">({tzAbbreviation})</span>}
              </span>
            )}
            {headerFacilityName && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {headerFacilityName}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ─── Status action area (right side) ────────────────────── */}
      <div className="shrink-0 flex flex-col items-end gap-1.5 mr-2" ref={statusMenuRef}>
        <span className={`text-base font-semibold ${STATUS_TEXT_COLOR[derivedStatus]}`}>{STATUS_LABEL[derivedStatus]}</span>
        {headerActionLabel && (
          <div className="relative flex items-stretch">
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-xs font-medium text-white leading-none ${
                isCheckedIn ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              onClick={handleHeaderAction}
            >
              {isCheckedIn ? <LogOut className="h-3.5 w-3.5" /> : <LogIn className="h-3.5 w-3.5" />}
              {headerActionLabel}
            </button>
            <button
              type="button"
              className={`inline-flex items-center rounded-r-md border-l border-white/30 px-1.5 text-white ${
                isCheckedIn ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              onClick={() => setStatusMenuOpen((v) => !v)}
              aria-label="More status options"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {statusMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 w-32 rounded-md border border-slate-200 bg-white shadow-lg py-1">
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                  onClick={handleHeaderCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-xs text-amber-600 hover:bg-amber-50"
                  onClick={handleHeaderNoShow}
                >
                  No Show
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="space-y-1.5">
      {selectedPatient ? (
        <div className="flex items-center gap-1.5">
          <h2 className="text-base font-semibold text-slate-900 truncate">
            {privacyMode ? '\u2014' : selectedPatient.displayName}
          </h2>
          {!privacyMode && <PatientMemberBadge patientId={selectedPatient.patientId} />}
          <ChangePatientButton active={changingPatient} onClick={() => setChangingPatient((v) => !v)} />
        </div>
      ) : (
        <h2 className="text-base font-medium text-slate-400 italic">Select a patient...</h2>
      )}
      <div className="flex flex-wrap items-center gap-2 text-base">
        {headerTypeName ? (
          <span className="inline-flex items-center gap-1 font-semibold text-blue-900">
            <Tag className="h-4 w-4" />
            {headerTypeName}
          </span>
        ) : (
          <span className="text-sm text-slate-400 italic">Select appointment type...</span>
        )}
        {watch('waitList') ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-amber-600">Wait List</span></> : null}
        {watch('compAppointment') ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-amber-600">COMP</span></> : null}
        {watch('firstAppointment') ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-blue-600">First Time</span></> : null}
        {/* Derived server-side and READ-ONLY — see the note on the other header. */}
        {appointment?.firstVisit ? <><span className="text-slate-300">–</span><span className="text-xs font-medium text-violet-600">First Visit</span></> : null}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {headerDateTimeStr ? (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {headerDateTimeStr}
            {tzAbbreviation && <span className="text-slate-400">({tzAbbreviation})</span>}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-slate-400 italic">
            <Calendar className="h-3 w-3" />
            Select date and time...
          </span>
        )}
        {headerFacilityName && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {headerFacilityName}
          </span>
        )}
      </div>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      {stageRecommendation && (
        <Modal
          isOpen
          onClose={handleDeclineStageChange}
          title="Update Plan Stage?"
          size="md"
          footer={(
            <div className="flex w-full justify-end gap-2">
              <Button variant="ghost" onClick={handleDeclineStageChange} disabled={stageApplying}>
                No, keep current stage
              </Button>
              <Button onClick={handleConfirmStageChange} loading={stageApplying}>
                Yes, advance to {stageRecommendation.targetStageName ?? `Stage ${stageRecommendation.targetStageId}`}
              </Button>
            </div>
          )}
        >
          <div className="p-4 text-sm text-slate-700 space-y-2">
            <p>
              Based on the <strong>{stageRecommendation.triggerAction}</strong> action you just performed,
              we recommend advancing the patient's plan to stage{' '}
              <strong>{stageRecommendation.targetStageName ?? stageRecommendation.targetStageId}</strong>.
            </p>
          </div>
        </Modal>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={modalTitle}
        size="xl"
        footer={footer}
        overlay={
          saveOverlay ? (
            <div className="flex flex-col items-center gap-3">
              {saveOverlay === 'saving' ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-slate-600">Saving…</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Saved</span>
                </>
              )}
            </div>
          ) : undefined
        }
      >
        <form id="unified-appt-form" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
          {giFormConfigured && !giFormUpToDate && watchedPatientId && watchedPatientId > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
              <span>This patient has not completed the current General Intake form.</span>
            </div>
          )}
          {validationBanner && (
            <div
              ref={validationBannerRef}
              role="alert"
              aria-live="polite"
              className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                <div className="flex-1">
                  <p className="font-semibold">{validationBanner.title}</p>
                  <ul className="mt-1 list-disc pl-5 text-xs">
                    {validationBanner.fields.map((f, idx) => (
                      <li key={`${f.label}-${idx}`}>
                        <span className="font-medium">{f.label}:</span> {f.message}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  className="text-red-600 hover:text-red-800"
                  onClick={() => setValidationBanner(null)}
                  aria-label="Dismiss validation errors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          {actionMessages.length > 0 && (
            <div className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" />
                <div>
                  <p className="font-semibold">Action Required</p>
                  <ul className="mt-1 list-disc pl-4">
                    {actionMessages.map((msg, i) => <li key={i}>{msg}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {(blockConflicts.length > 0 || blockWarnings.length > 0) && (() => {
            const categorized = categorizeConflicts(blockConflicts);
            const resourceErrors = categorized.resource.filter((c) => c.severity === 'error');
            const resourceWarnings = categorized.resource.filter((c) => c.severity !== 'error');
            // Categorize warnings too so general advisories (`no_resources`,
            // etc.) aren't mislabeled "Block warnings".
            const warningCat = categorizeConflicts(blockWarnings);
            return (
              <div className="mb-3 space-y-2">
                {categorized.block.length > 0 && (
                  <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    <p className="font-semibold">Block conflicts</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">
                      {categorized.block.map((c, idx) => (
                        <li key={`bc-${idx}`}>{c.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {resourceErrors.length > 0 && (
                  <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    <p className="font-semibold">Resource conflicts</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">
                      {resourceErrors.map((c, idx) => (
                        <li key={`re-${idx}`}>
                          <span className="font-medium">{c.resourceName ?? 'Resource'}:</span> {formatResourceConflictLine(c, displayTz)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {resourceWarnings.length > 0 && (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <p className="font-semibold">Resource overlaps</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">
                      {resourceWarnings.map((c, idx) => (
                        <li key={`rw-${idx}`}>
                          <span className="font-medium">{c.resourceName ?? 'Resource'}:</span> {formatResourceConflictLine(c, displayTz)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {categorized.other.length > 0 && (
                  <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    <p className="font-semibold">Other issues</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">
                      {categorized.other.map((c, idx) => (
                        <li key={`oi-${idx}`}>{c.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {warningCat.block.length > 0 && (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <p className="font-semibold">Block warnings</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">
                      {warningCat.block.map((w, idx) => (
                        <li key={`bw-${idx}`}>{w.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {warningCat.other.length > 0 && (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <p className="font-semibold">Warnings</p>
                    <ul className="mt-1 list-disc pl-5 text-xs">
                      {warningCat.other.map((w, idx) => (
                        <li key={`ow-${idx}`}>{w.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
          {/* ─── Quick links row ──────────────────────────────────────── */}
          {(appointment?.reschedulePending || (appointment?.patientPlanId != null && appointment.patientPlanId !== 0 && appointment?.planDisplayId)) && (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {appointment?.reschedulePending ? <Badge variant="warning">Reschedule Pending</Badge> : null}
              {appointment?.patientPlanId != null && appointment.patientPlanId !== 0 && appointment?.planDisplayId ? (
                <div className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline"
                    onClick={() => {
                      onClose();
                      navigate(`/plans/${appointment.patientPlanId}`);
                    }}
                  >
                    Plan #{appointment.planDisplayId}
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title="Remove plan from appointment"
                    onClick={handleRemovePlan}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {/* ─── Inline patient search ─────────────────────────────────── */}
          {(!selectedPatient || changingPatient) ? (
            <div className="mb-4 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <PatientSearchInput
                  selectedPatient={null}
                  onSelect={(p) => {
                    const previousPatientId = getValues('patientId');
                    const nextPatientId = p?.patientId ?? undefined;
                    setSelectedPatient(p);
                    setValue('patientId', nextPatientId, { shouldDirty: true });
                    // A different patient drops the plan (it was the previous
                    // patient's); the same patient picked again keeps it.
                    if (previousPatientId !== nextPatientId) {
                      const current: PlanSelection = {
                        planId: getValues('patientPlanId') || undefined,
                        chosenInPicker: planChosenInPickerRef.current,
                        autoPicked: planAutoPickedRef.current,
                      };
                      setFormPlan(planAfterPatientSelect(current, previousPatientId, nextPatientId), { shouldDirty: true });
                    }
                    setChangingPatient(false);
                  }}
                  placeholder={changingPatient ? 'Search for a different patient…' : 'Search patients…'}
                />
              </div>
              {/* `type="button"` is required — this sits inside
                  `#unified-appt-form`, so the default would submit it. */}
              <Button
                type="button"
                variant="secondary"
                leftIcon={<UserPlus className="h-4 w-4" />}
                title="Create a new patient"
                onClick={() => setAddPatientOpen(true)}
              >
                Add Patient
              </Button>
            </div>
          ) : null}

          {/* ─── Patient age banner ──────────────────────────────────── */}
          {patientAgeWarning && (
            <div className="mb-3 flex items-center gap-2 rounded border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs text-orange-800">
              <User className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{patientAgeWarning}</span>
            </div>
          )}

          {/* ─── Validation / Warnings ─────────────────────────────────── */}
          {(conflicts.length > 0 || warnings.length > 0) && (
            <SectionShell
              title={`Validation${conflicts.length + warnings.length > 0 ? ` (${conflicts.length + warnings.length})` : ''}`}
              collapsed={false}
              onToggle={() => toggleCollapsed('validation')}
            >
              {checkingConflicts ? (
                <p className="flex items-center gap-1.5 text-xs text-slate-400">
                  <AlertTriangle className="h-3.5 w-3.5 animate-pulse" /> Checking availability…
                </p>
              ) : null}
              {conflicts.length > 0 ? (
                <Alert variant="warning" title={`Conflicts (${conflicts.length})`}>
                  <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                    {conflicts.map((c) => (
                      <li key={`c-${c.appointmentId}`}>
                        {c.reason ?? c.patientName ?? 'Conflict'} — {formatTimeDisplay(c.startTime)} – {formatTimeDisplay(c.endTime)}
                        {c.resourceName ? ` (${c.resourceName})` : ''}
                      </li>
                    ))}
                  </ul>
                </Alert>
              ) : null}
              {warnings.length > 0 ? (
                <Alert variant="info" title={`Warnings (${warnings.length})`}>
                  <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                    {warnings.map((w, index) => (
                      <li key={`w-${w.appointmentId}-${index}`}>
                        {w.reason ?? 'Optional block rule'}
                        {w.resourceName ? ` (${w.resourceName})` : ''}
                      </li>
                    ))}
                  </ul>
                </Alert>
              ) : null}
            </SectionShell>
          )}

          {/* ─── Details ─────────────────────────────────────────────────── */}
          <SectionShell
            title="Details"
            collapsed={collapsed.details}
            onToggle={() => toggleCollapsed('details')}
          >
            {/*
              SCRUM-1406. Two columns at 375px gives each field ~160px, and the fields
              in a grid row stretch to the tallest — so the two-line "Start Time (EDT)"
              label block below made its neighbours look overlapped and half-covered.
              A single column below `md` removes both problems; `md:grid-cols-4` and the
              two-up tablet layout are untouched.
            */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-md:grid-cols-1">
              {/* Row 1 — when (date + timing). Start & End stay adjacent, duration far right. */}
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.date?.message}
                  />
                )}
              />
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <div>
                    {/*
                      This label is hand-rolled while End Time and Duration use their
                      component's own `label` prop, so the two never matched — the
                      asymmetry is why "End Time" and "Duration" read as covered in
                      SCRUM-1406. `min-w-0` + `shrink-0` keeps the label and its clock
                      button on one line inside the card instead of pushing the icon
                      past the right edge.
                    */}
                    <div className="flex items-center gap-1 mb-1.5">
                      <label className="min-w-0 text-sm font-medium text-slate-700">
                        Start Time ({tzAbbreviation})
                        <span className="ml-0.5 text-red-500" aria-hidden="true"> *</span>
                      </label>
                      <button
                        type="button"
                        title="Set to current time"
                        aria-label="Set start time to now"
                        className="shrink-0 p-0.5 text-slate-400 hover:text-indigo-600 transition-colors rounded touch-target-44 touch-target-inset"
                        onClick={() => {
                          // A zone that can't be read falls back to this computer's own clock.
                          const now = new Date();
                          const wall = wallClockPartsInTz(now, displayTz);
                          const h = wall ? wall.hour : now.getHours();
                          const m = wall ? wall.minute : now.getMinutes();
                          const nextSlot = m % 15 === 0 ? m : m + (15 - (m % 15));
                          const finalH = nextSlot >= 60 ? h + 1 : h;
                          const finalM = nextSlot >= 60 ? 0 : nextSlot;
                          field.onChange(
                            `${String(finalH % 24).padStart(2, '0')}:${String(finalM).padStart(2, '0')}`,
                          );
                        }}
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <TimePicker
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.startTime?.message}
                    />
                  </div>
                )}
              />
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    label={`End Time (${tzAbbreviation})`}
                    hint="Auto-fills; editable"
                    value={field.value ?? ''}
                    onChange={(next) => {
                      field.onChange(next);
                      if (watchedStartTime && next) {
                        const dur = computeDurationFromTimes(watchedStartTime, next);
                        if (dur) {
                          clearErrors('endTime');
                          setValue('duration', dur, { shouldValidate: true, shouldDirty: true });
                        } else {
                          setError('endTime', {
                            type: 'manual',
                            message: 'End time must be after start time',
                          });
                        }
                      }
                    }}
                    error={errors.endTime?.message}
                  />
                )}
              />

              <Input
                label="Duration (min)"
                type="number"
                min={1}
                required
                hint="Auto-fills from type"
                error={errors.duration?.message}
                {...register('duration', { valueAsNumber: true })}
              />

              {/* Row 2 — categorisation (facility + taxonomy + label). Group & Type stay adjacent. */}
              <Controller
                name="facilityId"
                control={control}
                render={({ field }) => (
                  <Select<number>
                    label="Facility"
                    placeholder="Select facility…"
                    options={facilityOptions}
                    value={field.value ?? null}
                    onChange={(v) => field.onChange(v)}
                    loading={loadingRefs}
                    clearable
                    searchable
                    error={errors.facilityId?.message}
                  />
                )}
              />
              <Controller
                name="appointmentTypeGroupId"
                control={control}
                render={({ field }) => (
                  <Select<number>
                    label="Group"
                    placeholder="Select group…"
                    options={groupOptions}
                    value={field.value ?? null}
                    onChange={(v) => field.onChange(v ?? undefined)}
                    loading={loadingRefs}
                    clearable
                    searchable
                    error={errors.appointmentTypeGroupId?.message}
                  />
                )}
              />
              <Controller
                name="appointmentTypeId"
                control={control}
                render={({ field }) => (
                  <Select<number>
                    label="Type"
                    placeholder={watchedGroupId ? 'Select type…' : 'Select a group first'}
                    options={typeOptions}
                    value={field.value ?? null}
                    onChange={(v) => field.onChange(v ?? undefined)}
                    loading={loadingRefs}
                    clearable
                    searchable
                    disabled={!watchedGroupId}
                    error={errors.appointmentTypeId?.message}
                  />
                )}
              />
              <Input
                label="Title"
                placeholder="Appointment title"
                required
                error={errors.name?.message}
                {...register('name')}
              />
              {planView.visible && (
                <div>
                  <Controller
                    name="patientPlanId"
                    control={control}
                    render={({ field }) => (
                      <Select<number>
                        label="Plan"
                        placeholder="Select plan…"
                        options={planOptions}
                        value={field.value && field.value > 0 ? field.value : null}
                        onChange={(v) => {
                          const picked = planAfterPick(v);
                          planChosenInPickerRef.current = picked.chosenInPicker;
                          planAutoPickedRef.current = false;
                          // A plan picked by hand ends "emptied by the user", so the
                          // only plan comes back as usual after a type round trip.
                          planClearedForPatientRef.current = picked.planId === undefined ? planPatientId : null;
                          // 0, not undefined: see setFormPlan.
                          field.onChange(picked.planId ?? 0);
                          clearErrors('patientPlanId');
                        }}
                        loading={loadingPatientPlans}
                        disabled={planView.noPlans}
                        required={planView.required}
                        clearable
                        searchable
                        error={errors.patientPlanId?.message ?? planView.message ?? undefined}
                      />
                    )}
                  />
                  {planView.loadFailed && (
                    <Button
                      type="button"
                      variant="link"
                      size="xs"
                      onClick={() => {
                        clearErrors('patientPlanId');
                        void refetchPatientPlans();
                      }}
                    >
                      Try again
                    </Button>
                  )}
                </div>
              )}

              <div className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-6">
                <Controller
                  name="firstAppointment"
                  control={control}
                  render={({ field }) => (
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <Switch
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                        className="data-[state=checked]:bg-blue-600"
                      />
                      <span className="text-xs font-medium text-slate-600">First Time</span>
                    </label>
                  )}
                />
                <Controller
                  name="compAppointment"
                  control={control}
                  render={({ field }) => (
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <Switch
                        checked={field.value ?? false}
                        onCheckedChange={async (checked) => {
                          field.onChange(checked);
                          if (appointmentId && selectedPatient?.patientId && !productFieldsChanged) {
                            compRequestInFlightRef.current = true;
                            showAutoSaveOverlay();
                            try {
                              await compAllProductsApi(
                                String(selectedPatient.patientId),
                                { apptID: String(appointmentId), type: checked ? 'all' : 'uncomp' },
                              );
                              reset(getValues(), { keepDirty: false });
                              loadNewProducts();
                            } catch (err) {
                              toast.error(getErrorMessage(err, 'Comp toggle failed'));
                            } finally {
                              compRequestInFlightRef.current = false;
                              hideAutoSaveOverlay();
                            }
                          }
                        }}
                        className="data-[state=checked]:bg-amber-500"
                      />
                      <span className="text-xs font-medium text-slate-600">Comp Products</span>
                    </label>
                  )}
                />
                <Controller
                  name="waitList"
                  control={control}
                  render={({ field }) => (
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <Switch
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                      <span className="text-xs font-medium text-slate-600">Wait List</span>
                    </label>
                  )}
                />
              </div>

              {watchedFirstAppointment && firstApptDefaults ? (
                <FirstApptPanel defaults={firstApptDefaults} />
              ) : null}

              {(() => {
                if (!newProductData) return null;
                const pts = Object.values(newProductData.products);
                const hasAnyProducts = pts.some(
                  (pt) => pt.pending.length > 0 || pt.disbursed.length > 0 || pt.purchaseOthers.length > 0,
                );
                const needsPurchase = pts.some(
                  (pt) => pt.pending.length > 0 || pt.purchaseOthers.length > 0,
                );
                const isComped = hasAnyProducts && (watchedCompAppointment || newProductData.compAppointment);
                const pendingTotal = pts.reduce(
                  (s, pt) => s + parseCurrency(pt.curRate) * pt.pending.length,
                  0,
                );
                const othersTotal = pts.reduce(
                  (s, pt) => s + parseCurrency(pt.curRate) * pt.purchaseOthers.length,
                  0,
                );
                const total = pendingTotal + othersTotal;
                const otherNames = pts
                  .filter((pt) => pt.purchaseOthers.length > 0)
                  .map((pt) => `${pt.productTypeName} (x${pt.purchaseOthers.length})`);
                return (
                  <div className="col-span-2 md:col-span-4 mt-2">
                    {isComped ? (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-amber-800">
                            All products for this appointment are comped.
                          </div>
                          {appointmentId && selectedPatient?.patientId && !productFieldsChanged && (
                            <button
                              type="button"
                              className="rounded bg-amber-500 px-3 py-1 text-xs font-medium text-white hover:bg-amber-600"
                              onClick={async () => {
                                compRequestInFlightRef.current = true;
                                showAutoSaveOverlay();
                                try {
                                  setValue('compAppointment', false);
                                  await compAllProductsApi(
                                    String(selectedPatient.patientId),
                                    { apptID: String(appointmentId), type: 'uncomp' },
                                  );
                                  reset(getValues(), { keepDirty: false });
                                  loadNewProducts();
                                } catch (err) {
                                  toast.error(getErrorMessage(err, 'Uncomp failed'));
                                } finally {
                                  compRequestInFlightRef.current = false;
                                  hideAutoSaveOverlay();
                                }
                              }}
                            >
                              Uncomp
                            </button>
                          )}
                        </div>
                      </div>
                    ) : needsPurchase ? (
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-slate-800">
                            Estimated Amount: {formatCurrency(total)}
                            {newProductData.isMember && (
                              <span className="ml-2 inline-block rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                                Member
                              </span>
                            )}
                          </div>
                          {appointmentId && !productFieldsChanged && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 rounded bg-blue-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-600"
                              onClick={() => setChargeOpen(true)}
                            >
                              <ShoppingCart className="h-3.5 w-3.5" />
                              Purchase
                            </button>
                          )}
                        </div>
                        {otherNames.length > 0 && (
                          <div className="text-xs text-slate-600">
                            <div>This patient has earlier appointments that require similar products</div>
                            <div className="font-semibold text-green-700">{otherNames.join(', ')}</div>
                            <div>You must purchase all products.</div>
                          </div>
                        )}
                      </div>
                    ) : hasAnyProducts ? (
                      <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-green-800">
                            This patient has enough products for this appointment.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                        <div className="font-medium text-slate-600">
                          No products required for this appointment.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </SectionShell>

          {/* ─── Status ─────────────────────────────────────────────────── */}
          <SectionShell
            title="Status"
            collapsed={collapsed.status}
            onToggle={() => toggleCollapsed('status')}
            right={<Badge variant={STATUS_BADGE[derivedStatus]} dot>{STATUS_LABEL[derivedStatus]}</Badge>}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0">
              <StatusRow
                label="Contacted"
                value={contactAttemptedAt}
                onChange={setContactAttemptedAt}
                facilityTz={displayTz}
              />
              <StatusRow
                label="Confirmed"
                value={confirmedAt}
                onChange={(v) => {
                  setConfirmedAt(v);
                  setConfirmedFlag(Boolean(v));
                }}
                facilityTz={displayTz}
                trailing={
                  <button
                    type="button"
                    title="Send confirmation"
                    disabled={sendingConfirmation || !canSendConfirmation}
                    onClick={handleSendConfirmation}
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium shrink-0 ${canSendConfirmation ? 'text-slate-500 hover:text-blue-600 hover:bg-blue-50' : 'invisible'}`}
                  >
                    <MessageSquare className="h-3 w-3" />
                    Send
                  </button>
                }
              />
              <StatusRow label="Check In" value={checkInAt} onChange={handleCheckInChange} facilityTz={displayTz} />
              <StatusRow label="Check Back" value={checkBackAt} onChange={setCheckBackAt} facilityTz={displayTz} />
              <StatusRow label="Check Out" value={checkOutAt} onChange={handleCheckOutChange} facilityTz={displayTz} />
              <StatusRow
                label="Cancelled"
                value={cancelledAt}
                onChange={handleCancelledAtChange}
                facilityTz={displayTz}
              />
              <div className="flex items-center gap-1.5 py-1">
                <Switch
                  checked={noShowFlag}
                  onCheckedChange={setNoShowFlag}
                />
                <span className="text-xs font-medium text-slate-600">No Show</span>
              </div>
            </div>

            {cancelledFlag && (
              <div className="mt-1 space-y-2">
                <Select<number>
                  label="Cancellation Reason"
                  required
                  placeholder="Select reason…"
                  options={[
                    { label: 'Other', value: 0 },
                    ...cancelReasons
                      .filter((r) => r.active)
                      .sort((a, b) => a.rOrder - b.rOrder)
                      .map((r) => ({ label: r.title, value: r.appointmentCancelReasonID })),
                  ]}
                  value={cancelReasonID}
                  onChange={(val) => {
                    setCancelReasonID(val);
                    if (val !== null && val !== 0) {
                      const reason = cancelReasons.find((r) => r.appointmentCancelReasonID === val);
                      setCancelledReason(reason?.title ?? '');
                    } else {
                      setCancelledReason('');
                    }
                  }}
                />
                {cancelReasonID === 0 && (
                  <Textarea
                    label="Reason Details"
                    required
                    placeholder="Enter the cancellation reason…"
                    rows={2}
                    maxLength={2000}
                    value={cancelledReason}
                    onChange={(e) => setCancelledReason(e.target.value)}
                  />
                )}
              </div>
            )}
          </SectionShell>

          {/* ─── Notes ──────────────────────────────────────────────────── */}
          {/* One section for everything note-related: the three free-text fields (the
              appointment-type note, this appointment's own `reason`, and a patient
              demographic answer) plus the linked clinical note records. Two sections
              with near-identical names made it unclear which held what. The count
              badge is the only signal when collapsed. */}
          <SectionShell
            title="Notes"
            collapsed={collapsed.notes}
            onToggle={() => toggleCollapsed('notes')}
            right={linkedNotes.length > 0 ? (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-100 px-1.5 text-[10px] font-bold text-blue-700">
                {linkedNotes.length}
              </span>
            ) : undefined}
          >
            {appointmentTypeNotes ? (
              <div className="mb-3">
                <label className="text-xs font-semibold text-slate-600">Appointment Type Note</label>
                <div className="mt-1 p-2 text-sm bg-slate-50 border border-slate-200 rounded whitespace-pre-line">
                  {appointmentTypeNotes}
                </div>
              </div>
            ) : null}
            <Textarea
              label="Appointment Notes"
              rows={4}
              maxLength={4000}
              showCount
              error={errors.reason?.message}
              {...register('reason')}
            />
            {patientNotes ? (
              <div className="mt-3">
                <label className="text-xs font-semibold text-slate-600">Patient Notes</label>
                <div className="mt-1 p-2 text-sm bg-slate-50 border border-slate-200 rounded whitespace-pre-line">
                  {patientNotes}
                </div>
              </div>
            ) : null}

            {/* Linked clinical note records. Separated by a hairline rather than a
                sub-header, since "Linked notes" below is a field label in the same
                style as its three siblings above. */}
            <div className="mt-3 border-t border-slate-200 pt-3">
              {canLinkNotes ? (
                <AppointmentLinkedNotes
                  appointmentId={appointmentId!}
                  patientId={notesPatientId!}
                  appointmentLabel={linkedNotesLabel}
                  notes={linkedNotes}
                  loading={linkedNotesLoading}
                  onAddNote={handleAddNote}
                />
              ) : (
                <>
                  <label className="text-xs font-semibold text-slate-600">Linked notes</label>
                  {/* Same shape as AppointmentProductsPanel's "Save the appointment to
                      add products." — there is no saved row to attach a note to yet,
                      and saying so beats the section silently having nothing in it. */}
                  <p className="mt-1 text-xs text-gray-500 italic">
                    Save the appointment to link notes.
                  </p>
                </>
              )}
            </div>
          </SectionShell>

          {/* ─── Products ───────────────────────────────────────────────── */}
          <SectionShell
            title="Required Products"
            collapsed={collapsed.products}
            onToggle={() => toggleCollapsed('products')}
          >
            {newProductData?.hasServiceCredit && newProductData?.hasRequiredProducts && (() => {
              const selectedMode = productModeOverride ?? newProductData.productMode;
              const canSwitch = newProductData.canSwitch !== false;
              const productsOpts = newProductData.requiredProductsOption ?? [];
              const creditOpt = newProductData.serviceCreditOption ?? null;
              const productsLabel = productsOpts
                .map((o) => `${o.quantity} × ${o.productTypeName}`)
                .join(' + ');
              const creditLabel = creditOpt
                ? `${creditOpt.quantity} × ${creditOpt.productTypeName}`
                : '';
              // A side is available only when every product in it has an active
              // cost item. An unpriced side stays visible but can't be selected.
              const productsAvailable =
                productsOpts.length > 0 &&
                productsOpts.every((o) => o.available !== false);
              const creditAvailable = !!creditOpt && creditOpt.available !== false;
              const options: Array<{
                mode: ProductMode;
                label: string;
                fallback: string;
                available: boolean;
              }> = [
                { mode: 'products', label: productsLabel, fallback: 'Products', available: productsAvailable },
                { mode: 'credit', label: creditLabel, fallback: 'Package', available: creditAvailable },
              ];
              return (
                <div className="mb-3 rounded-md border border-slate-200 bg-white p-2.5">
                  <div className="mb-1.5 text-xs font-medium text-slate-600">
                    Required for this appointment
                  </div>
                  <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
                    {options.map((opt) => {
                      const active = selectedMode === opt.mode;
                      // Disabled when the switch is locked (disbursed) or when
                      // this option is unpriced — but the active option is always
                      // shown (so an invalid current set is still visible).
                      const locked =
                        (!canSwitch && !active) || (!opt.available && !active);
                      return (
                        <button
                          key={opt.mode}
                          type="button"
                          disabled={locked}
                          aria-pressed={active}
                          title={!opt.available ? 'No active price for this option' : undefined}
                          onClick={() => {
                            if (canSwitch && !active && opt.available) applyProductMode(opt.mode);
                          }}
                          className={
                            'px-3 py-1.5 text-xs font-medium ' +
                            (opt.mode === 'credit' ? 'border-l border-slate-300 ' : '') +
                            (active
                              ? opt.available
                                ? 'bg-blue-500 text-white'
                                : 'bg-amber-500 text-white'
                              : locked
                                ? 'cursor-not-allowed bg-slate-50 text-slate-400'
                                : 'bg-white text-slate-600 hover:bg-slate-50')
                          }
                        >
                          {opt.label || opt.fallback}
                        </button>
                      );
                    })}
                  </div>
                  {!canSwitch ? (
                    <div className="mt-1.5 text-xs italic text-slate-400">
                      Undisburse the current products to switch.
                    </div>
                  ) : newProductData.attachedInvalid ? (
                    <div className="mt-1.5 text-xs italic text-amber-600">
                      The current selection is no longer available — switch to the
                      available option above.
                    </div>
                  ) : null}
                </div>
              );
            })()}
            <AppointmentProductsPanel
              patientId={String(selectedPatient?.patientId ?? '')}
              apptID={appointmentId ? String(appointmentId) : ''}
              data={newProductData}
              loading={newProductsLoading}
              error={newProductsError}
              onRefresh={loadNewProducts}
              showProductButtons={!productFieldsChanged && (newProductData?.showProductButtons ?? false) && derivedStatus !== 'cancelled' && derivedStatus !== 'no_show'}
              onAddProducts={derivedStatus !== 'cancelled' && derivedStatus !== 'no_show' ? () => setAddProductsOpen(true) : undefined}
              onPurchase={derivedStatus !== 'cancelled' && derivedStatus !== 'no_show' ? () => setChargeOpen(true) : undefined}
              compOverride={watchedCompAppointment}
              onAutoSaveStart={showAutoSaveOverlay}
              onAutoSaveEnd={hideAutoSaveOverlay}
              isCheckedOut={!!appointment?.checkedOutAt}
            />
          </SectionShell>

          {/* ─── Patient Inventory ────────────────────────────────────────── */}
          <SectionShell
            title="Patient Products"
            collapsed={collapsed.patientInventory}
            onToggle={() => toggleCollapsed('patientInventory')}
          >
            <PatientInventoryPanel data={patientInventory} />
          </SectionShell>

          {/* ─── Resources ──────────────────────────────────────────────── */}
          <SectionShell
            title="People / Resources"
            collapsed={collapsed.resources}
            onToggle={() => toggleCollapsed('resources')}
            right={
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-slate-400" />
                {selectedResourceIds.size > 0 && (
                  <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                    {selectedResourceIds.size}
                  </span>
                )}
              </span>
            }
          >
            {/* AR-4: type-first row list (legacy appointment-schedule.js
                L6682-6688). Each row pairs a Type select with a filtered
                Resource select and a per-row conflict dot. */}
            <ResourceRowList
              rows={resourceRowListRows}
              resources={resources}
              resourceTypeLabels={resourceTypeLabels}
              resourceConflicts={resourceConflicts}
              conflictChecking={checkingConflicts}
              onChangeType={handleChangeRowType}
              onChangeResource={handleChangeRowResource}
              onRemoveRow={handleRemoveRow}
              onAddRow={handleAddResourceRow}
            />
          </SectionShell>

          {/* ─── Tasks — Past Due / Due Today ─────────────────────────────── */}
          {selectedPatient?.patientId && (
            <SectionShell
              title="Tasks — Past Due / Due Today"
              collapsed={collapsed.tasks}
              onToggle={() => toggleCollapsed('tasks')}
              headerClassName={humanTasksDueSoon.length > 0
                ? "flex items-center justify-between gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-t-md cursor-pointer select-none"
                : undefined
              }
              right={humanTasksDueSoon.length > 0 ? (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-100 px-1.5 text-[10px] font-bold text-red-700">
                  {humanTasksDueSoon.length}
                </span>
              ) : undefined}
            >
              <div className="flex items-center justify-end mb-2">
                <a
                  href={`/patients/${selectedPatient.patientId}?section=tasks`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  View All Tasks
                </a>
              </div>
              {humanTasksDueSoon.length === 0 ? (
                <p className="text-xs italic text-slate-400">No past due or due today human tasks.</p>
              ) : (
                /*
                  Five columns with no wrapper and no card-mode fallback — this is a raw
                  table, not the shared DataTable. At 375px it sets the modal body's
                  width and drags the whole form sideways, which is the other half of
                  "the section below is cut off" in SCRUM-1408. Scrolling it inside its
                  own box below `md` contains that; desktop keeps the plain table.
                */
                <div className="max-md:overflow-x-auto">
                <table className="w-full text-[11px] max-md:min-w-[32rem]">
                  <thead>
                    <tr className="border-b border-slate-200 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                      <th className="py-1.5 pr-2 text-left">Task</th>
                      <th className="py-1.5 px-2 text-left">Assigned To</th>
                      <th className="py-1.5 px-2 text-left">Status</th>
                      <th className="py-1.5 px-2 text-left">Priority</th>
                      <th className="py-1.5 pl-2 text-left">Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {humanTasksDueSoon.map((task: TaskRow) => {
                      const dueStr = task.lastDue ?? task.dueDate ?? null;
                      const dueFmt = dueStr && dueStr !== '0000-00-00'
                        ? new Date(dueStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—';
                      const isPastDue = task.derivedStatus === 'Past Due';
                      const priorityText = task.priority === 3 ? 'High' : task.priority === 1 ? 'Low' : 'Medium';
                      return (
                        <tr key={task.planTaskID}>
                          <td className="py-1.5 pr-2 font-medium text-slate-800">{task.name}</td>
                          <td className="py-1.5 px-2 text-slate-600 max-w-[100px] truncate">{task.assignedToName ?? '—'}</td>
                          <td className="py-1.5 px-2">
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                              isPastDue ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {isPastDue ? 'Past Due' : 'Due Today'}
                            </span>
                          </td>
                          <td className="py-1.5 px-2">
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                              task.priority === 3 ? 'bg-orange-100 text-orange-700'
                                : task.priority === 1 ? 'bg-slate-100 text-slate-500'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {priorityText}
                            </span>
                          </td>
                          <td className="py-1.5 pl-2 text-slate-500 whitespace-nowrap">{dueFmt}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </SectionShell>
          )}

          {/* ─── Shortcodes ─────────────────────────────────────────────── */}
          {isEditMode ? (
            <SectionShell
              title="Shortcodes"
              collapsed={collapsed.shortcodes}
              onToggle={() => toggleCollapsed('shortcodes')}
            >
              <ShortcodesPanel
                shortcodes={shortcodes}
                values={shortcodeValues}
                onChange={setShortcodeValues}
              />
            </SectionShell>
          ) : null}

          {/* ─── History ────────────────────────────────────────────────── */}
          {isEditMode ? (
            <SectionShell
              title="Appointment History"
              collapsed={collapsed.history}
              onToggle={() => toggleCollapsed('history')}
              right={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    loadHistory();
                  }}
                >
                  Refresh
                </Button>
              }
            >
              <HistoryPanel history={history} loading={historyLoading} />
            </SectionShell>
          ) : null}

          {/* ─── Block banner ──────────────────────────────────────────── */}
          {isBlock ? (
            <Alert variant="info" className="mt-2">
              This is a block-time entry. Edit block-specific settings from the Block Time modal.
            </Alert>
          ) : null}
        </form>
      </Modal>

      {/* ─── Purchase handoff ────────────────────────────────────────────── */}
      {chargeOpen && selectedPatient?.patientId ? (
        <ChargePaymentModal
          open={chargeOpen}
          onClose={() => setChargeOpen(false)}
          patientId={selectedPatient.patientId}
          patientName={selectedPatient.displayName ?? undefined}
          appointmentId={appointmentId ?? undefined}
          prefillItems={chargePrefill}
          facilityId={watchedFacilityId}
          isMember={!!newProductData?.isMember}
          onSaved={() => {
            setChargeOpen(false);
            loadNewProducts();
          }}
        />
      ) : null}

      {/* ─── Add products popup ──────────────────────────────────────────── */}
      {addProductsOpen && appointmentId ? (
        <AddProductsModal
          isOpen={addProductsOpen}
          onClose={() => setAddProductsOpen(false)}
          appointmentId={appointmentId}
          patientId={selectedPatient?.patientId ?? null}
          facilityId={watchedFacilityId ?? null}
          appointmentDate={productsPacificDate || null}
          onRefresh={loadNewProducts}
          currentEstimatedCost={(() => {
            if (!newProductData) return 0;
            const pts = Object.values(newProductData.products);
            return pts.reduce(
              (s, pt) => s + parseCurrency(pt.curRate) * (pt.pending.length + pt.purchaseOthers.length), 0,
            );
          })()}
        />
      ) : null}

      {/* ─── Product shortage alert ─────────────────────────────────────────── */}
      <Modal
        isOpen={productAlertCost !== null}
        onClose={() => setProductAlertCost(null)}
        size="sm"
        hideCloseButton
        closeOnBackdrop={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setProductAlertCost(null)}>
              Do It Later
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setProductAlertCost(null);
                setChargeOpen(true);
              }}
              disabled={!selectedPatient?.patientId || !appointmentId}
            >
              Purchase Now
            </Button>
          </div>
        }
      >
        <div className="flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Insufficient Products</h3>
            <p className="text-sm text-slate-500">
              This patient does not have enough products for this appointment. It is recommended they purchase before services are rendered.
            </p>
            <p className="mt-2 text-base font-semibold text-slate-800">
              Estimated cost: {formatCurrency(productAlertCost ?? 0)}
            </p>
          </div>
        </div>
      </Modal>

      {/* ─── Unsaved changes confirm dialog ───────────────────────────────── */}
      <Modal
        isOpen={showUnsavedConfirm}
        onClose={() => setShowUnsavedConfirm(false)}
        size="sm"
        hideCloseButton
        closeOnBackdrop={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowUnsavedConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmClose}>
              Discard Changes
            </Button>
          </div>
        }
      >
        <div className="flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Unsaved Changes</h3>
            <p className="text-sm text-slate-500">
              You have unsaved changes. Are you sure you want to close? Your changes will be lost.
            </p>
          </div>
        </div>
      </Modal>

      {/* ─── Delete confirm dialog ──────────────────────────────────────────── */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        size="sm"
        hideCloseButton
        closeOnBackdrop={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
              Go Back
            </Button>
            <Button variant="danger" onClick={confirmHardDelete} loading={deleting}>
              Delete Permanently
            </Button>
          </div>
        }
      >
        <div className="flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Permanently Delete Appointment?</h3>
            <p className="text-sm text-slate-500">
              This action is permanent and cannot be undone. The appointment record will be completely removed.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              If you want to keep a record of this appointment, consider <strong>canceling</strong> it instead — the record will be retained and can be referenced later.
            </p>
          </div>
        </div>
      </Modal>

      {/* ─── Find Plan picker (legacy `selectPatientButton`) ───────────────── */}
      {planPickerOpen ? (
        <Modal
          isOpen={planPickerOpen}
          onClose={() => setPlanPickerOpen(false)}
          title="Find Plan"
          size="md"
        >
          <div className="space-y-2">
            {planPickerLoading ? (
              <p className="text-sm text-slate-500">Loading plans…</p>
            ) : planPickerOptions.length === 0 ? (
              <p className="text-sm text-slate-500">
                This patient has no plans yet. Use “New Plan” to create one.
              </p>
            ) : (
              <ul className="divide-y divide-slate-200 rounded border border-slate-200">
                {planPickerOptions.map((p) => (
                  <li key={p.planId}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => handleAttachPlanFromPicker(p.planId)}
                    >
                      <span className="flex flex-col">
                        <span className="font-medium text-slate-800">
                          {p.name?.trim() || `Plan #${p.planId}`}
                        </span>
                        <span className="text-xs text-slate-500">
                          {[p.planTypeName, p.stageName, p.date].filter(Boolean).join(' · ') || `#${p.planId}`}
                        </span>
                      </span>
                      <Plus className="h-4 w-4 text-blue-600" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      ) : null}

      {/* ─── New Plan modal (legacy `addPlanButton` → `loadNewLeadForm`) ───── */}
      {newPlanOpen && selectedPatient?.patientId ? (
        <PlanCreateModal
          open={newPlanOpen}
          onClose={() => setNewPlanOpen(false)}
          defaultPatientId={selectedPatient.patientId}
          onCreated={(plan) => {
            void handleNewPlanCreated(plan.planId);
          }}
        />
      ) : null}

      {/* ─── Add Patient modal (the `/leads/new` intake form, embedded) ─────
          Must stay AFTER the main <Modal> above: these all portal to
          document.body at z-50, so JSX order is paint order. */}
      {addPatientOpen ? (
        <PatientCreateModal
          open
          onClose={() => setAddPatientOpen(false)}
          defaultFacilityId={watchedFacilityId ?? null}
          onCreated={(created) => {
            void handlePatientCreated(created);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Legacy compatibility aliases. Kept so existing call sites (SchedulePage,
 * PatientDetailPage, AppointmentsPage, PlanDetailPage) continue to work.
 * The nested `Edit` action in AppointmentDetailModal collapses to a no-op —
 * the unified modal is already editable inline.
 */
export interface AppointmentDetailModalCompatProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment;
  onUpdated: (appointment: Appointment) => void;
  onDeleted?: () => void;
  /** Threaded through to UAM so legacy `users_filters_sched.privacyModeToggle`
   *  parity (scheduler-appt-details.php L682) follows the calendar-side
   *  setting. See docs/audit/appt-details-parity.md §1.1 + P5. */
  privacyMode?: boolean;
  /** P8 — Find Available Time callback. See
   *  `UnifiedAppointmentModalProps.onFindAvailableTime`. */
  onFindAvailableTime?: (seed: AppointmentNextAvailableSeed) => void;
}

export function AppointmentDetailModal(props: AppointmentDetailModalCompatProps) {
  return (
    <UnifiedAppointmentModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      appointment={props.appointment}
      privacyMode={props.privacyMode}
      onFindAvailableTime={props.onFindAvailableTime}
      onSaved={(next) => {
        if (next) props.onUpdated(next);
        else props.onDeleted?.();
      }}
    />
  );
}

export interface AppointmentCreateModalCompatProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (appointment: Appointment) => void;
  appointment?: Appointment;
  defaultDate?: string;
  defaultStartTime?: string;
  defaultPatientId?: number;
  defaultPatient?: PatientSummary | null;
  defaultResourceIds?: number[];
  defaultPatientPlanId?: number;
  defaultEndTime?: string;
  defaultDuration?: number;
  defaultName?: string;
  defaultReason?: string;
  defaultFacilityId?: number;
  defaultAppointmentTypeId?: number;
  defaultAppointmentTypeGroupId?: number;
  /** Threaded through to UAM so legacy `users_filters_sched.privacyModeToggle`
   *  parity (scheduler-appt-details.php L682) follows the calendar-side
   *  setting. See docs/audit/appt-details-parity.md §1.1 + P5. */
  privacyMode?: boolean;
  /** P8 — Find Available Time callback. See
   *  `UnifiedAppointmentModalProps.onFindAvailableTime`. */
  onFindAvailableTime?: (seed: AppointmentNextAvailableSeed) => void;
}

export function AppointmentCreateModal(props: AppointmentCreateModalCompatProps) {
  return (
    <UnifiedAppointmentModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      appointment={props.appointment}
      defaultDate={props.defaultDate}
      defaultStartTime={props.defaultStartTime}
      defaultEndTime={props.defaultEndTime}
      defaultDuration={props.defaultDuration}
      defaultName={props.defaultName}
      defaultReason={props.defaultReason}
      defaultFacilityId={props.defaultFacilityId}
      defaultAppointmentTypeId={props.defaultAppointmentTypeId}
      defaultAppointmentTypeGroupId={props.defaultAppointmentTypeGroupId}
      defaultPatientId={props.defaultPatientId}
      defaultPatient={props.defaultPatient}
      defaultPatientPlanId={props.defaultPatientPlanId}
      defaultResourceIds={props.defaultResourceIds}
      privacyMode={props.privacyMode}
      onFindAvailableTime={props.onFindAvailableTime}
      onSaved={(next) => {
        if (next) props.onSuccess(next);
      }}
    />
  );
}
